import {
  isCounterpartyScopedRole,
  isDepartmentScopedRole,
  isObjectScopedRole,
  OPERATOR_STATUS_TRANSITIONS,
  REQUEST_MODULES,
  requestStatusRollbacks,
  requestStatusTransitions,
  roleLabels,
  ROLES,
  VEHICLE_REQUEST_TYPES,
  type RequestModule,
  type RequestStatus,
  type Role,
  type VehicleRequestType,
} from './enums';
import {
  COUNTERPARTY_TYPES,
  counterpartyTypeLabels,
  type CounterpartyType,
} from './counterparties';
import {
  canAttachAddon,
  ROLE_ADDON_BASE_ROLES,
  ROLE_ADDONS,
  roleAddonLabels,
  type RoleAddon,
} from './role-addons';

/**
 * Единая модель прав портала (ADR 0021, дополнена ADR 0038).
 *
 * Право — это «что учётка может делать», область видимости («над какими строками») задаётся
 * отдельно: штаб работает со своим объектом, внешний исполнитель — с заявками своего
 * контрагента. Смешивать их в одном списке нельзя: право проверяется до запроса в БД
 * (preHandler), а область — по конкретной строке, и ошибка тут даёт либо 403 на своей же
 * заявке, либо доступ к чужой.
 *
 * Права выдаются не роли, а **субъекту доступа** — паре «роль + тип контрагента учётки»
 * (ADR 0038). У всех ролей, кроме внешнего исполнителя, контрагента нет и субъект совпадает с
 * ролью; у исполнителя тип контрагента решает, в каком модуле он работает: оператор вывоза
 * ведёт заявки вывоза, арендодатель ТС — заявки на технику. Роль отвечает «кем человек работает
 * в портале», тип контрагента — «по какому предмету»; вторая роль-близнец на каждый модуль
 * дублировала бы первое ради второго.
 *
 * Матрица живёт в общем пакете, потому что источник правды должен быть один: API проверяет
 * права на каждом маршруте, портал по той же матрице решает, показывать ли пункт меню и
 * кнопку. Разъехавшиеся списки ролей — это либо кнопка, ведущая в 403, либо действие,
 * которое видно, но запрещено на сервере (или, что хуже, наоборот).
 */
export const PERMISSIONS = [
  // Справочники — единый модуль: доступ выдаётся на весь раздел, а не на отдельную вкладку.
  // Чтение нужно всем ролям: без него не заполнить форму заявки (объект, тип ТС, тип контейнера).
  'directories.read',
  'directories.write',
  /**
   * Обмен справочником файлом Excel (ADR 0073). Отдельно от `directories.write`, потому что это
   * не заведение записи, а действие над справочником целиком: выгрузка уносит из портала все его
   * строки разом (у водителей — с ФИО и СНИЛС), загрузка меняет сотни строк одним нажатием.
   *
   * Права два, а не одно: риск у них разный, и разведёнными их можно раздать по отдельности —
   * выгрузку когда-нибудь откроют тому, кто ведёт справочник, а загрузку не откроют никому,
   * кроме администратора. Одно право такой возможности не оставило бы.
   */
  'directories.export',
  'directories.import',

  // Вывоз мусора
  'wasteRequests.read',
  'wasteRequests.create',
  'wasteRequests.update',
  'wasteRequests.delete',
  'wasteRequests.status',
  'wasteRequests.assignOperator',
  /**
   * Примечание исполнителя в заявке (ADR 0053): вторая сторона комментария. Своё право, а не
   * часть `wasteRequests.update`: исполнитель заявку не редактирует (ADR 0038), а сказать
   * площадке «будем после 15:00» должен.
   */
  'wasteRequests.operatorComment',
  /**
   * Разбор распознанных талонов вывоза (ADR 0114, Р25): подтвердить, исправить, отклонить, принять
   * расхождение, увидеть замечания сверки и отобрать в реестре «требующие разбора».
   *
   * Отдельное право, а не часть `wasteRequests.status`, и причина здесь не в аккуратности
   * нарезки. `wasteRequests.status` есть **у внешнего исполнителя** (`COUNTERPARTY_TYPE_PERMISSIONS`
   * ниже, тип `operator`): именно он отмечает заявку выполненной и прикладывает талон (ADR 0010,
   * ADR 0038). Повесь мы разбор на него — проверяемый закрывал бы замечания к собственной бумаге,
   * то есть сверка проверяла бы того, кто ею управляет.
   *
   * Право управляет и **видимостью**: замечания уходят в DTO только с ним, распознанные значения
   * тоже (они такой же результат сверки, как и замечания), история разбора фильтруется им же —
   * иначе замечание просочилось бы через журнал заявки, — а фильтр списка без него **отклоняется**,
   * а не игнорируется: молчаливое игнорирование вернуло бы полный список и читалось бы как
   * «требующих разбора нет».
   *
   * Права мало: оно говорит, что человек разбирает талоны, но не говорит, **чьи**. Заявку, файл,
   * `kind = 'ticket'` и объектную с операторской область проверяет каждая ручка отдельно (Р26).
   *
   * Открыть сверку площадке — выдать ей это же право набором полномочий (ADR 0106); отдельного
   * кода для этого не потребуется.
   */
  'wasteRequests.ticketReview',

  /**
   * Аудит распознавания талонов (ADR 0137, план `docs/waste-ticket-audit-plan.md` §4.1): сводка
   * точности по полям, когорты конфигураций конвейера, разбор споров, лупа на скан и выгрузка
   * наблюдений в CSV.
   *
   * **Отдельное право, а не часть `wasteRequests.ticketReview`, и разводит их не размер модуля, а
   * область.** Разбор — работа над своей заявкой: держатель `ticketReview` закрывает замечания к
   * тем талонам, которые ему и так видны, и чьи они, проверяет каждая ручка отдельно (Р26). Аудит
   * работает поперёк: он отвечает на вопрос «где машина ошибается», а ответ на него собирается по
   * всем площадкам сразу — считай его по своему куску выборки, и доля исправлений мерила бы не
   * модель, а объект. Держатель поэтому видит адреса всех площадок и сканы талонов всех
   * исполнителей; это цена сквозного аудита, выбранная заказчиком 26.08.2026 (§4.1).
   *
   * Отсюда и способ выдачи: право не выдаётся **ни одной роли**, кроме администратора, у которого
   * оно есть по построению (`admin: [...PERMISSIONS]`). Живёт оно только одноцелевым системным
   * набором `waste_ticket_audit` (`MODULE_GRANTS` в `grants.ts`), а тот администратор выдаёт
   * поимённо, строкой с автором и временем. Впиши его кто-нибудь в `WASTE_REQUEST_PERMISSIONS`
   * рядом с разбором — и сквозная картина досталась бы всей диспетчерской разом, то есть мимо
   * поимённой выдачи, ради которой набор и заведён.
   *
   * **Страницы администрирования право не касается.** Аудит открывается окном из самого модуля
   * вывоза (§5), а не вкладкой `/admin`, поэтому строки в `ADMIN_PAGE_PERMISSIONS` у него нет и не
   * будет: тот список отвечает на вопрос «пускать ли на страницу администрирования», а этой
   * страницы аудит не открывает вовсе.
   *
   * Право отвечает на два вопроса и только на них: показывать ли кнопку в панели вывоза и пускать
   * ли в шесть ручек аудита. Ни статуса заявки, ни её содержимого оно не трогает — все шесть
   * читающие.
   */
  'wasteRequests.ticketAudit',

  // Заказ ТС
  'vehicleRequests.read',
  'vehicleRequests.create',
  'vehicleRequests.update',
  'vehicleRequests.delete',
  'vehicleRequests.status',
  /** Виза руководителя строительства: без неё заявку не берут в работу (ADR 0025). */
  'vehicleRequests.approve',

  /**
   * Недельная заявка на технику: документ-основание над заказами ТС — площадка недельным пакетом
   * решает, что остаётся, что уезжает и что нужно добавить, а виза руководителя строительства
   * этот пакет применяет (продлевает сроки и порождает заказы).
   *
   * Права свои, а не `vehicleRequests.*`, и причина не в аккуратности, а в области видимости.
   * `vehicleRequests.read` есть у наблюдателя, у оператора вывоза и у арендодателя через тип
   * контрагента, а `vehicleRequestVisibilityWhere` для ролей без объектной и отдельской оси
   * возвращает «ограничений нет». Переиспользуй мы его — наблюдатель, задуманный как «только
   * просмотр», получил бы полный список недельных планов всех площадок, а арендодатель — чужие
   * планы работ. Своя группа даёт свой предикат области, у которого последняя ветка не «видит
   * всё», а «не видит ничего».
   *
   * Виза отдельным правом по той же причине, что у заявок ТС: согласование — решение заказчика со
   * стороны объекта, а не того, кто заявку обрабатывает. Здесь оно к тому же необратимо
   * (применение идёт той же транзакцией), и раздавать его вместе с ведением состава нельзя.
   */
  'weeklyRequests.read',
  'weeklyRequests.create',
  'weeklyRequests.update',
  'weeklyRequests.approve',

  /**
   * Механизация — аренда малой механизации (план `docs/mechanization-module-plan.md`, Р9).
   * Заявка здесь и есть аренда (Р1): её заводит площадка или отдел, договорённость с
   * арендодателем ставит офис, а фактическую выдачу и возврат отмечает он же.
   *
   * Пять прав устроены как у соседних модулей, шестое — своё. **Продление разведено со сменой
   * статуса намеренно**: продлить аренду — это не «поправить срок», а согласие платить дальше, и
   * звонит арендодателю с этим диспетчер (решение заказчика). Лежи продление внутри `.status`, его
   * получил бы и менеджер, а отобрать его составом набора было бы нечем.
   *
   * Своей группы прав у арендодателя нет и не будет: учёток за таким контрагентом не заводят вовсе
   * (Р6, пустая строка в `COUNTERPARTY_TYPE_PERMISSIONS`) — справочная запись нужна, чтобы заявка
   * знала, кому звонить, и чтобы деньги складывались по арендодателям.
   */
  'mechRequests.read',
  'mechRequests.create',
  'mechRequests.update',
  'mechRequests.delete',
  'mechRequests.status',
  'mechRequests.extend',
  /**
   * Виза ответственного лица площадки: без неё аренду не берут в работу, и ею же подписывается
   * продление (план `docs/mechanization-approval-and-grants-plan.md`, Р1, Р3, Р10).
   *
   * Право своё, а не переиспользованное `vehicleRequests.approve`, хотя подпись у площадки одна:
   * право отвечает за ручку модуля, в котором она живёт, — иначе витрина механизации показывала бы
   * право «Заказа ТС», а манифест маршрутов перестал бы отвечать, чем защищена ручка. Общая
   * ответственность человека выражается **составом набора** («Виза объекта» несёт обе подписи), а
   * не одним правом поперёк модулей.
   */
  'mechRequests.approve',

  // Водители (ADR 0037). Отдельно от справочников: в карточке водителя лежат персональные
  // данные — СНИЛС, номер удостоверения, — и открывать их каждому, кому нужен список типов ТС,
  // нельзя. По той же причине права нет ни у наблюдателя, ни у объектных ролей.
  'drivers.read',
  'drivers.write',

  // Путевые листы (ADR 0037). Журнал учёта и аннулирование — своими правами: в листе те же
  // персональные данные, что в карточке водителя, а испорченный бланк списывает не всякий, кто
  // берёт заявки в работу. Выдача отдельного права не имеет: лист рождается переводом заявки в
  // работу, и разрешает его `vehicleRequests.status`.
  'waybills.read',
  'waybills.cancel',
  /**
   * Подшить к бланку скан: заполненный заказчиком оборот ЭСМ-2, отметки 4-П, акт. Своё право, а
   * не `waybills.read`: смотреть журнал может и тот, кто документы к нему не подшивает.
   */
  'waybills.files',
  /**
   * Выписать лист по рейсу без заявок — бланк с реквизитами машины, водителя и даты, но с пустым
   * заданием (ADR 0071). Своё право у одного этого случая: обычная выписка так и остаётся частью
   * работы по заявке, а пустой бланк расходует номер строгой отчётности ни на что — задание в нём
   * появится от руки, и портал уже не отвечает за то, что в нём напишут.
   */
  'waybills.issueBlank',
  /**
   * Коррекция задним числом (ADR 0101): аннулировать и перевыписать лист прошедшего дня, исправить
   * машину и водителя состоявшегося рейса, завести и подвинуть дату заявки в прошлое.
   *
   * Право одно на **всё** задним числом, а не по объекту (Р14): круг лиц у этих действий один, и
   * разложив их по трём правам, мы получили бы диспетчера, который дату заявки исправить может, а
   * лист под ту же дату — нет. Отдельно от `waybills.cancel`: то отвечает на «списать испорченный
   * бланк сегодня», это — на «переписать прошедший день». Имя от бланков потому, что бланк самое
   * тяжёлое из списка.
   */
  'waybills.correct',
  /**
   * Глубже `WAYBILL_CORRECTION_DAYS` (Р37). Делит не объекты, а глубину: 30 дней — дневная работа
   * диспетчера, дальше административная правка давностью в квартал. Без `waybills.correct` смысла
   * не имеет и в наборах ролей идёт вместе с ним — так же читает их `backdateGuard`.
   *
   * Правом, а не проверкой роли: предикат остаётся без ветвления по ролям, а на вопрос «кому
   * положено» отвечает матрица — там же, где на все прочие.
   */
  'waybills.correctBeyondLimit',

  /**
   * Заявки на обслуживание оргтехники (ADR 0085). Прав четырнадцать, и дробность у них не от
   * аккуратности, а от того, что заявку ведут три разные стороны.
   *
   * `assign` и `approveEstimate` — решения заказчика: кого позвать чинить и согласны ли мы на эти
   * деньги. `estimate` — работа исполнителя: собрать смету, предъявить её и закрыть работы.
   * `status` — движение по остальным дугам (приёмка, возврат на доработку, отмена); что именно
   * доступно, решает не матрица, а коридор субъекта, потому что одно и то же право у сервиса и у
   * оператора открывает разные переходы.
   *
   * `files` отдельно от `update` по той же причине, что `waybills.files`: исполнитель заявку не
   * редактирует, но акт и счёт подшивает — и подшивает даже после приёмки, когда правки закрыты.
   */
  'serviceRequests.read',
  /**
   * «Видит деньги и объём работ заявок своей области» (план
   * `docs/office-equipment-requester-card-plan.md`, Р1). Право не открывает ни одной ручки — оно
   * расширяет ВЫДАЧУ уже открытых: без него карточка, список и история приходят читателю без
   * состава работ, сумм, согласования и закрытых видов документов.
   *
   * ПРАВОМ, А НЕ ПРОВЕРКОЙ НАБОРА ПО МЕСТУ — по той же причине, что записана в миграции
   * `0231_service_request_without_equipment_grant.sql`: набор отвечает на вопрос «кто», право — на
   * «что можно». Условие «у человека надстройка ИТ», написанное в маршруте, невидимо витрине прав,
   * не снимается составом набора и не выдаётся поимённо.
   *
   * И НЕ ОТРИЦАНИЕМ «нет ни одного из `estimate|approveEstimate|assign|status|execute|approveIt`».
   * Такое правило ломается молча: первое же новое право модуля (за полгода их прибавилось четыре —
   * `hold`, `urgency`, `createWithoutEquipment`, `approveIt`) открыло бы деньги тому, кому их не
   * открывали, и ни один тест этого не заметил бы — ошибка была бы в НЕПЕРЕЧИСЛЕННОМ.
   *
   * Второе положительное основание финансовой аудитории правом не выражается вовсе и здесь его нет:
   * назначенному исполнителю деньги открывает ФАКТ назначения на конкретную заявку
   * (`serviceRequestAudienceOf` в `service-requests.ts`). Выдай мы ему право, базовая роль заказчика
   * показала бы ему суммы всех заявок своей площадки или отдела — в том числе тех, на которые его
   * никто не назначал.
   *
   * **Требует `serviceRequests.read`** (`PERMISSION_REQUIRES`): право видеть деньги заявки,
   * выданное тому, кто заявок не видит, не открывает ни одной двери и обещает то, чего нет.
   */
  'serviceRequests.finance',
  'serviceRequests.create',
  /**
   * «Заводит заявку без аппарата» (план расходников и закупки, Р5; ADR 0146, решение 6).
   *
   * Правом, а не проверкой набора по месту. Набор отвечает на вопрос «кто», право — на «что
   * можно», и смешение этих слоёв уже стоило модулю оргтехники двух дыр: условие «у автора есть
   * надстройка» живёт в маршруте, невидимо витрине прав и не снимается составом набора. Право же
   * видно в сетке, выдаётся поимённо и отбирается тем же способом.
   *
   * **Требует `serviceRequests.create` (`PERMISSION_REQUIRES`).** Аппарат необязателен не сам по
   * себе, а внутри заведения: ручка одна — `POST /service-requests`, и её статический страж
   * спрашивает `serviceRequests.create`. Держатель без него не завёл бы ничего, и право обещало бы
   * то, чего сервер не пустит, — ровно та ошибка, на которой споткнулась первая редакция плана
   * закупок.
   *
   * Проверяет **сервер**, а не форма: схема заведения принимает пустой аппарат всегда, а маршрут
   * отбивает его `403` у того, у кого права нет. Иначе запрет держался бы на звёздочке в поле, то
   * есть ни на чём.
   */
  'serviceRequests.createWithoutEquipment',
  'serviceRequests.update',
  'serviceRequests.delete',
  'serviceRequests.assign',
  'serviceRequests.estimate',
  /**
   * Переписать объём работ по графам (план
   * `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р2; ответ В9 заказчика от
   * 09.09.2026) — ручка `PUT /:id/estimate/breakdown` и кнопка «Разложить по графам».
   *
   * ЗАЧЕМ СВОЁ ПРАВО. Подрядчик присылает объём работ письмом — одной свободной записью с общей
   * суммой, без цен по позициям, — и раскладывает её на графы «Ведение»: письмо у него перед
   * глазами, а исполнитель ту же смету уже предъявил и правит её своим правом. Работа эта чужая
   * автору объёма работ и своя тому, кто ведёт заявку.
   *
   * ПОЧЕМУ «ВЕДЕНИЮ» НЕ ВЫДАЁТСЯ `serviceRequests.estimate`, ЧЕМ ВОПРОС ЗАКРЫЛСЯ БЫ ОДНОЙ СТРОКОЙ.
   * Потому что то право означает не «я правлю таблицу строк», а «я АВТОР объёма работ, то есть
   * сторона исполнителя»: по нему `actsAsServiceExecutor` пускает администратора доводить зависшую
   * заявку, по нему же считаются сторона обсуждения и очередь «Ждут меня». Выданное «Ведению», у
   * которого есть `serviceRequests.approveEstimate`, оно превратило бы согласование в подпись под
   * собственной работой — ровно то смешение, ради которого две стороны и разведены.
   *
   * ЧТО ПРАВО НЕ ДАЁТ: ни возврата сметы в правку, ни предъявления, ни подписи. Всё это ручка
   * делает сама и только там, где ревизия согласована: поднимает номер, снимает подпись,
   * пересчитывает итог и ставит новое предъявление — иначе заявка повисла бы без очереди, потому
   * что ни `reopen`, ни `submit` держателю не открыты. Инвариант ADR 0133 при этом цел: подписанное
   * содержимое под ПРЕЖНЕЙ ревизией не меняется, меняется — под новой.
   */
  'serviceRequests.estimateRewrite',
  'serviceRequests.approveEstimate',
  /**
   * Виза отдела ИТ: нужен ли внешний ремонт вообще (план модернизации, Р51). Отдельно от
   * `approveEstimate` — это разные решения и разные люди: ИТ отвечает «зовём ли сервис», оператор
   * — «согласны ли на эти деньги». Слитые в одно право, они превратили бы двухступенчатое
   * согласование в одну подпись.
   */
  'serviceRequests.approveIt',
  'serviceRequests.status',
  /**
   * Заморозка: «Отложена» и возврат в тот статус, из которого заявку отложили (план переделки
   * цикла, §6.3).
   *
   * Своим правом, а не «выдадим ИТ-службе `serviceRequests.status`»: то открывает **весь**
   * операторский коридор — приёмку работы, отмену из любого статуса, согласование сметы, — а
   * ИТ-службе по матрице положены ровно заморозка и возобновление. Слитые в одно, они дали бы
   * сисадмину возможность закрыть заявку за заказчика или отменить её без причины.
   */
  'serviceRequests.hold',
  /**
   * Отмена заявки службой, которая её ведёт, но не принимает работу (workflow-fixes plan Р2, owner
   * walkthrough of 08.09.2026: «нет возможности отменить заявку с комментарием»).
   *
   * ITS OWN RIGHT, NOT `serviceRequests.status` FOR THE IT SERVICE. `status` opens the WHOLE operator
   * corridor — accepting the work, sending it back for rework, and together with
   * `requests.rollbackStatus` the administrative rollbacks — and it is exactly what the owner asked
   * to take away from the IT service in the same walkthrough (item 6). The corridor of this right is
   * `SERVICE_CANCEL_TRANSITIONS`: the three cancel arcs of the operator and nothing beyond them. The
   * reason, and for a repair the decision «what we do instead», are asked by the cancel window as
   * they are for everyone: the permission opens the door, not the lack of an explanation.
   *
   * Not for the contractor: his side answers instead of the others (`allowedServiceStatusTransitions`)
   * and never reaches this branch, whatever set he is given by hand. «Ведение» does not need it —
   * it holds `status` — and does not get it: one right in two sets of the same person would read as
   * a second source of the same arc.
   */
  'serviceRequests.cancel',
  /**
   * Флаг «Срочная» — поставить и снять (Н12 набросков).
   *
   * Сегодня флаг приходит вместе с `serviceRequests.update`, то есть всякому, кто правит заявку, и
   * составом набора этого не отобрать: оставить ИТ-службе правку, забрав срочность, можно только
   * отдельным правом. Оно же отвечает на требование постановки «внешний исполнитель приоритет не
   * ставит» — у сервисной компании нет ни `update`, ни этого права.
   */
  'serviceRequests.urgency',
  /**
   * «Может быть назначен исполнителем заявки» — поимённый слой назначения (план переделки цикла,
   * §7.1).
   *
   * Ходов заявки право в одиночку не открывает: их открывает **факт назначения**, а право работает
   * с ним в паре. Нужно оно ручке назначения: без него администратор мог бы назначить исполнителем
   * заказчика и тем выдать ему ходы, которых нет ни в одном наборе. Обратная сторона той же пары —
   * ради неё право и заведено: у переведённого сотрудника снятие набора закрывает ходы, не трогая
   * историю назначений.
   *
   * Сервисной компании право не выдаётся, и это выбор, а не пропуск (`COUNTERPARTY_TYPE_PERMISSIONS`
   * ниже): у неё назначается контрагент, а не человек, поимённых строк у её сотрудников не бывает
   * вовсе, и требование права от них либо отобрало бы у подрядчика сегодняшние ходы, либо дописало
   * бы ему право ради проверки, которая для него не выполняется ни при каком назначении.
   */
  'serviceRequests.execute',
  'serviceRequests.files',

  /**
   * Справочник оргтехники (ADR 0085): что стоит по кабинетам и площадкам.
   *
   * Своя пара прав, а не общие справочниковые. Чтение отдельно от `directories.read`, потому что то
   * есть у всех ролей — включая коменданта и исполнителя чужого модуля, — а карточка единицы
   * рассказывает и про её обслуживание. Ведение отдельно от `directories.write`, потому что то
   * открывает **весь** раздел (объекты, контрагенты, прайс, техника), а ответственному за
   * оргтехнику нужен один справочник: иначе он либо получит лишнее, либо не сможет завести только
   * что купленный принтер.
   */
  'officeEquipment.read',
  'officeEquipment.write',

  /**
   * «Подтверждает перемещение техники» (план `docs/office-equipment-move-from-request-plan.md`,
   * Р1): запись переезда единицы — откуда, куда, когда и почему — из карточки заявки и из
   * справочника.
   *
   * Отдельно от `officeEquipment.write`, потому что это разные обязанности. Сисадмин, приехавший
   * по заявке, видит, где аппарат стоит на самом деле, — а заводить, переименовывать и списывать
   * карточки парка ему при этом незачем. Пока перемещение открывалось ведением, сузить круг было
   * нечем: `write` даёт разом заведение, правку, типы, модели и архив.
   *
   * **Требует только `officeEquipment.read`** (`PERMISSION_REQUIRES`): перемещать вслепую нельзя —
   * подтверждают то, что видят, — а вести справочник ради подтверждения переезда не обязательно.
   * Требуй право `write`, круг остался бы прежним, и заводить его было бы незачем.
   *
   * Дверей у действия две — карточка заявки и справочник, — и замок у обеих этот. Две двери к
   * одному действию с разными замками означают отсутствие замка.
   */
  'officeEquipment.move',

  /**
   * «Сообщает о технике, которой нет в справочнике» (план
   * `docs/office-equipment-candidate-plan.md`, Р8): заявитель отправляет **кандидата** вместе со
   * своей заявкой, а карточку по нему заводит проверяющий.
   *
   * Право, а не проверка набора по месту, — по той же причине, что у `createWithoutEquipment`:
   * набор отвечает на вопрос «кто», право — на «что можно», и смешение этих слоёв уже стоило
   * модулю двух дыр. Ведения справочника оно не даёт вовсе: `POST /office-equipment` и
   * `PATCH /office-equipment/:id` держателю по-прежнему отвечают 403 — в том и смысл, что дверь в
   * парк остаётся одна и открывает её решение человека с `officeEquipment.write`.
   *
   * **Требует `serviceRequests.create` и `officeEquipment.read`** (`PERMISSION_REQUIRES`).
   * Первое — потому что кандидат заводится ВНУТРИ заведения заявки (ручка одна, `POST
   * /service-requests`), и держатель без него не завёл бы ничего. Второе — потому что «сообщаю об
   * отсутствии» имеет смысл только у того, кто мог искать: без чтения справочника вход по
   * построению производит дубли.
   */
  'officeEquipment.propose',
  /**
   * «Проверяет сообщения об отсутствующей технике» (план кандидатов, Р8): очередь проверки, правка
   * заявленных реквизитов до решения и три решения — завести карточку, объединить с существующей,
   * отклонить с причиной.
   *
   * **Требует `officeEquipment.write`, и это главный замок обеих задач.** Проверка заканчивается
   * записью в парк, то есть ровно тем действием, которое `write` и охраняет; право проверки без
   * него стало бы вторым, необъявленным входом в справочник — тем самым обходом, ради устранения
   * которого кандидаты и заводятся. Отсюда же следует, кому право достаётся: профилю «Ведение»
   * (`office_equipment_operator`), а не ИТ-службе — у той `officeEquipment.write` нет намеренно
   * (план профилей, §5.2), и выдать ей `review` значило бы выдать заодно весь парк.
   */
  'officeEquipment.review',
  /**
   * Разбор очереди «Письма устройств» (план `docs/office-equipment-mail-telemetry-plan.md`, Р30):
   * привязать письмо к аппарату, отметить просмотренным, перечитать.
   *
   * ПОЧЕМУ ОТДЕЛЬНОЕ ПРАВО, А НЕ `officeEquipment.review`. Первая редакция плана обещала обойтись
   * существующим — и обещала неверно: `review` подписано «проверяет сообщения об отсутствующей
   * технике» и требует `officeEquipment.write`, то есть ведения всего парка. Выдать очередь через
   * него значило бы либо закрыть её от ИТ-службы, для которой она и заводится (у той `write` нет
   * намеренно), либо раздать ИТ-службе запись во весь справочник ради одной кнопки.
   *
   * ПОЧЕМУ ЧТЕНИЕ СЮДА НЕ ВХОДИТ. Показания и события в карточке открыты по `officeEquipment.read`
   * всем, кому открыта сама карточка: это справочные данные, и прятать их за правом разбора
   * значило бы прятать от тех, ради кого приём затеян. Право закрывает ДЕЙСТВИЯ очереди — те, что
   * решают, чья наработка чьей карточке принадлежит.
   */
  'officeEquipment.telemetry',

  /**
   * Номенклатура расходников — картриджи и тонеры (план расходников, Р10). Два права, и оба
   * отдельно от `officeEquipment.write` намеренно: то открывает **весь парк** — карточки техники,
   * модели, перемещения, — а номенклатуру ведёт один человек, которому парк править незачем.
   *
   * Разведены между собой по той же причине, по которой разведены смета и её согласование: завести
   * позицию и поправить остаток на складе — разные работы. `manage` — заведение, правка, гашение и
   * совместимость с моделями; `stock` — только ручная корректировка остатка, то есть единственная
   * ручка, пишущая событие в журнал от имени человека.
   *
   * Чтения среди них нет: список и карточка расходника открываются широким `officeEquipment.read`
   * — подобрать картридж при заведении заявки должен каждый, кому видна оргтехника.
   */
  'officeEquipmentConsumables.manage',
  'officeEquipmentConsumables.stock',

  /**
   * Плановая закупка расходников (план расходников и закупки, Р12): заведение документа, правка
   * черновика, проведение в снабжение, закрытие, отмена и чтение списка — всё одним правом. Сторона
   * у закупки одна, и разводить эти четыре хода по разным правам значило бы предложить
   * администратору выбор, которого в работе не существует.
   *
   * **Почему своё право, а не `serviceRequests.*`.** Закупка — свой документ со своей таблицей и
   * своими ручками, и первая редакция плана споткнулась ровно об это: право, приписанное к модулю
   * заявок, упиралось в статический страж `POST /service-requests` — субъект с парой «закупка +
   * чтение», но без `serviceRequests.create`, не завёл бы ничего. Со своей ручкой вопрос снимается:
   * `serviceRequests.create` закупке не нужен вовсе.
   *
   * Чтения среди прав закупки нет по той же причине, что у номенклатуры: список и карточку
   * открывает то же `…manage` — у документа одна сторона, и «смотрю, но не веду» тут не работа, а
   * половина её. Зато `officeEquipment.read` объявлен требованием (`PERMISSION_REQUIRES`): закупка
   * показывает номенклатуру, и вести то, чего не видишь, — дыра, ради которой таблица требований и
   * заведена.
   *
   * Область у права нет: документ глобальный (Р9), и делить его площадками значило бы обещать
   * область, которой нет ни у остатка, ни у потребности.
   */
  'officeEquipmentPurchases.manage',

  /**
   * Гараж (ADR 0076): срез дня — чем заняты собственная техника и водители на выбранную дату.
   *
   * Своё право, а не сумма прав тех списков, из которых срез собран. Причина та же, по которой
   * отдельно стоят `drivers.read` и `waybills.read`: в строке гаража видно, кто за рулём, и это те
   * же персональные данные, что в карточке водителя. Связка «`drivers.read` и `vehicleRequests.read`»
   * ответила бы на вопрос «есть ли из чего собрать», а не «положен ли человеку раздел» — и
   * закрыть гараж, оставив справочник водителей, стало бы нечем.
   *
   * Само право — по-прежнему одно и только на чтение, а вот «действий в разделе нет вовсе» больше
   * не правда, и перестало быть ею дважды. Сначала приёмка показаний (ADR 0103): одометр и
   * моточасы правят из строки техники, и делает это `vehicleReadings.write`. Теперь
   * автозапчасти (план чеков, Р4): чеки поставщиков ведёт `autoParts.manage`, уничтожает —
   * `autoParts.delete`. Правило раздела при этом не изменилось — каждое действие открывает своё
   * право, а `garage.read` отвечает на один вопрос «положен ли человеку раздел», — но читать его
   * как «в гараже ничего не делают» нельзя. И оно шире, чем вход в срез дня: им же открываются
   * список чеков, карточка чека и окно машины, потому что ответить «чем платили за фильтр» должен
   * всякий, кому виден гараж.
   */
  'garage.read',

  /**
   * Receipt-backed auto-part accounting (ADR 0216): supplier receipts, assignment of each line
   * to a vehicle or warehouse, application documents, scans, and deletion marks.
   *
   * Предмет права переосмыслен, а не заведён заново. До заморозки (Р2, Р22) `autoParts.manage`
   * называло ведение складской номенклатуры, а рядом стояло `autoParts.stock` — движение остатка.
   * The retired ADR 0134 catalog and its manually edited balance remain frozen. ADR 0216 does
   * not revive them: a new lot is a receipt line, and its balance is derived from immutable
   * application documents. There is still no direct stock-balance action for a separate
   * `autoParts.stock` permission to protect.
   * ADR 0217 later added `manager`: the temporary role already belongs to actual employees who
   * need expanded operational access, so the permission comes from the role instead of a named
   * grant set. The warehouse extension does not narrow that audience.
   *
   * Отвергнуто было завести рядом `autoPartReceipts.manage`: в окне учётки остались бы два права,
   * из которых одно не открывает ни одной ручки, — а «право, которое ничего не даёт» портал
   * однажды уже показывал, и разбирались с ним дольше, чем стоило бы его снять.
   *
   * Строки `grant_permissions` со снятым правом при этом остаются в базе, и это ожидаемое
   * состояние ровно на один выкат (Р26): у выпуска заморозки нет ни одной миграции — иначе откат
   * кода перестал бы быть откатом, — а чистит таблицу миграция C следующего выката. Доступа
   * сирота не даёт: неизвестный код отсекается фильтром `isPermission` в `principal.ts` ещё до
   * сборки принципала. Чистят её не ради безопасности, а ради каталога — набор, в котором
   * числится несуществующее право, показывается администратору дающим больше, чем даёт.
   *
   * Права у модуля по-прежнему два, но граница между ними другая — не «реквизиты и движение», а
   * «ведение и уничтожение»: `autoParts.manage` заводит чек, назначает его строки,
   * оформляет применение со склада, правит до первого применения и ставит пометку; `autoParts.delete`
   * стирает чек насовсем и не выдан ни одной роли (Р4а).
   * Деления внутри ведения нет вовсе, и условных прав у модуля не осталось ни одного: чек
   * правится целиком, а «только администратор» выражено самим правом, а не условием по эффекту
   * запроса.
   *
   * Чтения среди них нет: список чеков, склад, месячная форма, карточка и окно машины
   * открываются широким `garage.read` — ответить «чем платили за фильтр» должен всякий, кому
   * виден гараж. Зато оба права объявлены требующими `garage.read` (`PERMISSION_REQUIRES` в
   * `grants.ts`): право вести (и уничтожать) то, чего не видишь, — дыра, ради которой таблица
   * требований и заведена.
   *
   * Набора полномочий у пары, в отличие от расходников оргтехники, нет, и это решение, а не
   * пропуск: там номенклатуру ведёт один человек, которого назначают поимённо, здесь заказчик
   * назвал не человека, а службу — «учёт и обслуживание ведут механики», — а служба в портале уже
   * описана ролями `mechanic` и `chief_mechanic`. Набор ради аудитории, совпадающей с ролями,
   * добавил бы шаг выдачи, ничего не разделив.
   */
  'autoParts.manage',

  /**
   * Удаление чека на автозапчасти (план `docs/auto-part-receipts-plan.md`, Р4а) — право, а **не**
   * проверка роли в обработчике.
   *
   * Требование заказчика звучит «удаляет только администратор», и написать его условием прямо в
   * ручке вышло бы на строку короче. Цена этой строки — слепая зона сверки: манифест доступа знает
   * восемь видов условия (`ACCESS_CONDITION_KINDS` в `access-manifest.ts`), и «условия по роли»
   * среди них нет вовсе. Ручка, спрятанная за проверкой роли, попала бы в перебор прав как
   * открытая всякому держателю права ведения, а сверка «манифест ↔ факт» этого не заметила бы:
   * она сличает объявленные права с фактически спрошенными, а `if` про роль ей невидим. Правом же
   * ограничение читается одинаково манифестом, матрицей доступа и окном учётки.
   *
   * Ни одной роли право не выдано: администратор получает его по построению
   * (`admin: [...PERMISSIONS]`), а отдельной строки в `ROLE_PERMISSIONS` право не получает.
   * **Одного этого мало.**
   * Состав назначаемого полномочия произволен (ADR 0106), барьер у него один, — поэтому право
   * стоит в `NON_GRANTABLE_PERMISSIONS` (`grants.ts`) шестым. Без той строки «только
   * администратор» держалось бы на том, что никто не собрал такой набор, то есть ни на чём.
   * Причина запрета та же, что у `records.purge`: удаление чека необратимо — вместе с записью
   * уходят строки и отвязываются сканы, — и ошибка держателя набора не лечится ничем, кроме
   * резервной копии.
   *
   * Пометка на удаление этим правом не ставится: её ставит и снимает `autoParts.manage`, и
   * разведены две работы намеренно. Механик, увидевший в чеке ошибку, обязан уметь сказать об
   * этом, не уничтожая документ; слей мы пометку с удалением — перед необратимым действием не
   * осталось бы второго шага, а «пометить» и «стереть» стали бы одним доступом.
   *
   * `garage.read` объявлен требованием и ему (`PERMISSION_REQUIRES`): уничтожать то, чего не
   * видишь, — дыра ровно того же вида, что и править невидимое.
   */
  'autoParts.delete',

  /**
   * Кабинет водителя (ADR 0102) — второй контур портала. Права два и оба узкие: роль `driver`
   * не получает больше ничего, включая `directories.read`, который есть у всех остальных ролей.
   * Отсюда обязательство API кабинета: подписи приходят готовыми, справочники ему не нужны.
   */
  'driverCabinet.read',
  'driverCabinet.submit',

  /**
   * Показания техники (ADR 0103): одометр, моточасы и заправленное за смену. Права свои, а не
   * `garage.*`: гараж только показывает эти строки, а ведёт их свой модуль — и правка чужого
   * показания это не «смотреть день парка», а учётное действие с историей и причиной.
   */
  'vehicleReadings.read',
  'vehicleReadings.write',

  /**
   * The readings report as an Excel book: work of equipment by request days (ADR 0240, which
   * replaced the five-sheet book of ADR 0180).
   *
   * A separate right, not part of `vehicleReadings.read`: the book covers the whole fleet and every
   * vehicle request of an arbitrary period, with drivers' and customers' names, and leaves by mail
   * — it is taking data out, not looking at it, the same reasoning as `directories.export`. Since
   * ADR 0241 it opens the «Отчёт» button in the garage header, and the roles the owner named carry
   * it: the manager and both mechanic roles by their sets, the administrator by construction
   * (`admin: [...PERMISSIONS]`); anyone else gets it by a grant.
   *
   * Requires `vehicleReadings.read` and `vehicleRequests.read` (`PERMISSION_REQUIRES`): exporting
   * what one sees on no screen is not allowed. The route also refuses a narrow area.
   */
  'vehicleReadings.export',

  /**
   * Сводная аналитическая выгрузка по заказчикам (`docs/analytics-summary-export-plan.md`, Р2).
   *
   * Своё право, и требований (`PERMISSION_REQUIRES`) у него нет намеренно: книга сводит три модуля
   * сразу — заказ техники, вывоз мусора и механизацию, — и требование читать все три закрыло бы её
   * тому, ради кого она делается, то есть службе, которая ни одной заявки не ведёт. Взамен из книги
   * убраны персональные данные вовсе: ни ФИО водителей, ни телефонов ответственных, только
   * площадки, техника, количества и деньги (Р2).
   *
   * Область у права всегда общая: свод, в котором часть площадок невидима, несравним сам с собой
   * между двумя людьми, поэтому обладателю с объектной областью ручка отвечает отказом, а не
   * усечённой книгой (Р3).
   */
  'analytics.export',

  /**
   * Техобслуживание техники по пробегу (Р14): журнал записей ТО, скан акта и сводка по машине.
   *
   * Право своё и самодостаточное, а не часть `vehicleReadings.*`. Служба главного механика ведёт ТО
   * и больше в модуле показаний не делает ничего; выдай ей чтение показаний — и вместе с формой ТО
   * откроются приёмка, журналы, выгрузки и фотографии приборных панелей, то есть весь модуль ради
   * одной формы. Независимое право, которое нельзя получить, не взяв соседнее, независимо только на
   * бумаге.
   *
   * Обратная сторона решения — обязательство ручек: сводка ТО отдаёт всё, что нужно про
   * обслуживание, включая последний одометр с датой (Р14б), и не ссылается на DTO показаний. Поле,
   * исчезающее по чужому праву, было бы тем же смешением, только спрятанным в сериализатор (Р14а).
   */
  'vehicleMaintenance.read',
  'vehicleMaintenance.write',

  // Действия над удалёнными и закрытыми записями — общие для заявок и справочников
  /** Видеть удалённые записи (архив): списки с includeDeleted, карточка удалённой заявки. */
  'archive.read',
  'archive.restore',
  /** Вернуть закрытую («Выполнена»/«Отменена») заявку в предыдущий статус. */
  'requests.rollbackStatus',
  /** Удаление записи насовсем, минуя пометку «удалена»: восстановить её уже нечем. */
  'records.purge',

  // Файлы: удаление чужого файла, не привязанного к заявке (свой удаляет и автор загрузки).
  'files.manageAny',

  /**
   * Карантин файла: разбор инцидента с ошибочно загруженным документом (план
   * `docs/office-equipment-on-site-and-invoice-estimate-plan.md`, Р6, п. 4).
   *
   * Карантин закрывает содержимое ВСЕМ — автору загрузки, исполнителю, «Ведению», администратору
   * области, — и делает это раньше любой ветки видимости в `canAccessFile`. Право отвечает на
   * единственный оставшийся вопрос: кому карантинный файл всё-таки открыт, потому что инцидент
   * кто-то обязан разобрать. Без него заперший документ сам не смог бы посмотреть, что именно
   * заперли, и «скрыто по обращению» было бы неотличимо от «файла не было».
   *
   * **Одно право на три операции — постановку, снятие и доступ к содержимому, — и это решение.**
   * Поставить карантин, не видя содержимого, значит запирать наугад; снять его, не видя, — тем
   * более. Разведи их на `files.manageAny` (постановка) и право аудита (чтение) — и держатель
   * первого прятал бы основание денежного решения от всех, включая себя, а вернуть доступ мог бы
   * только держатель второго. Несимметричная пара «закрыть может один, открыть другой» — это не
   * разделение обязанностей, а способ потерять документ.
   *
   * **Почему не `files.manageAny`.** Тот приходит МАТРИЦЕЙ роли — он есть у менеджера и диспетчера
   * целиком, — и карантин достался бы диспетчерской разом. Разбор инцидента с персональными
   * данными ведёт НАЗВАННЫЙ человек: право поэтому не выдаётся ни одной роли, кроме
   * администратора (`admin: [...PERMISSIONS]`), и живёт одноцелевым системным набором
   * `files_quarantine_audit` (`MODULE_GRANTS`), который администратор выдаёт поимённо, строкой с
   * автором и временем. Тот же приём и по той же причине, что у `wasteRequests.ticketAudit`.
   *
   * **Каждый доступ по этому праву пишется в журнал** (`file.quarantine_access`,
   * `routes/files.ts`): разбор инцидента сам обязан быть прослеживаемым — иначе сквозное право
   * превратилось бы в тихий обход всех областей сразу, ведь карантинный файл бывает из любого
   * модуля.
   *
   * Область у права своей нет и быть не может: карантин ставят по файлу, а файл приходит из любого
   * модуля — сужать его объектом значило бы оставить без разбора ровно тот инцидент, где документ
   * попал на чужую площадку. Отсюда и ограничение выдачи: только ролям без собственной оси
   * (`GRANT_SCOPE_MATRIX`, модуль `files`).
   */
  'files.quarantineAudit',

  // Администрирование
  'users.manage',
  'audit.read',
  /**
   * Рассылки: расписания, история запусков и отладочная отправка письма себе или другому
   * администратору. Право своё, а не `users.manage`: рассылку настраивает тот, кто отвечает за
   * оповещения, и это не обязательно тот же человек, который выдаёт доступы. Пока оба права есть
   * только у администратора, но разъехаться им ничто не мешает.
   */
  'mailings.read',
  'mailings.manage',
  /**
   * Ведение списка руководств пользователя (`docs/manuals-plan.md`): заведение, правка, снятие с
   * публикации и удаление строки со ссылкой на документ.
   *
   * Право одно, парного чтения у него нет намеренно. Список руководств отвечает на вопрос «как
   * пользоваться порталом», и закрыть его правом значило бы выдать это право каждому вошедшему —
   * то есть завести право, которое ничего не решает (то же рассуждение, что у журнала обновлений,
   * ADR 0077 §5). Читает список `authenticate`, а `manuals.manage` отличает того, кто список
   * ведёт, и им же открывается вкладка ведения — модуль закрывается своим правом (ADR 0021).
   *
   * Право назначаемое (ADR 0106): в `NON_GRANTABLE_PERMISSIONS` его нет, потому что руководства
   * пишет не обязательно тот, кто выдаёт доступы. Из этого следует и отдельный модуль витрины
   * `manuals` — место в модуле `admin` сделало бы входным правом набора невыдаваемый
   * `users.manage` (см. `permission-catalog.ts`).
   */
  'manuals.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Есть ли такое право в словаре — страж границы «база → код» (ADR 0106).
 *
 * Права назначаемого полномочия хранятся в `grant_permissions` **текстом**, и справочника прав в
 * базе нет намеренно: он был бы второй копией закрытого словаря и разъехался бы с ним в первый же
 * выкат, переименовавший право. Обратная сторона решения — сироты: выкат, снявший право из
 * `PERMISSIONS`, оставляет в базе строки, которым в коде больше ничего не соответствует. CHECK по
 * списку прав их бы не допустил, но превратил бы снятие права в отказ базы, то есть в падение
 * каждой проверки доступа.
 *
 * Поэтому сироты живут в таблице и отсеиваются **при чтении**: доступа такая строка не даёт
 * (право, которого нет в словаре, не стоит ни на одном маршруте), а витрина показывает её как
 * «право больше не существует». Предикат, а не `includes` по месту и не `as Permission` на границе:
 * приведение типом пообещало бы, что фильтровать нечего, и первое же снятое право стало бы
 * значением `Permission`, которого в `PERMISSIONS` нет, — с ним `permissionsFor` и витрина начали
 * бы рассказывать про доступ то, чего в коде уже нет.
 */
const PERMISSION_SET: ReadonlySet<string> = new Set(PERMISSIONS);

export const isPermission = (value: string): value is Permission => PERMISSION_SET.has(value);

const DIRECTORY_PERMISSIONS = ['directories.read', 'directories.write'] as const;

/**
 * Ведение водителей (ADR 0037). Идёт вместе со справочниками у тех, кто обрабатывает заявки:
 * водителя выбирают при переводе заявки в работу, и заводит его тот же человек, который
 * выписывает путевой лист.
 */
const DRIVER_PERMISSIONS = ['drivers.read', 'drivers.write'] as const;
/**
 * Показания техники (ADR 0103) идут тем же, кто ведёт день парка: гараж, журнал машины и правка
 * чужого показания — работа одного человека, а не трёх разных.
 */
const VEHICLE_READING_PERMISSIONS = ['vehicleReadings.read', 'vehicleReadings.write'] as const;

/**
 * Техобслуживание (Р14). Чтение и ведение идут парой у всех четырёх ролей, которым право положено:
 * запись ТО заводит тот же человек, который смотрит, когда обслуживали в прошлый раз, — и роли,
 * которой журнал ТО показывают только читать, в замысле нет. Разойдётся такая — придёт поимённой
 * строкой, как `weeklyRequests.read` у наблюдателя.
 */
const VEHICLE_MAINTENANCE_PERMISSIONS = [
  'vehicleMaintenance.read',
  'vehicleMaintenance.write',
] as const;

/**
 * Auto-part receipt management is one indivisible permission: create, edit, recognize scans, and
 * request deletion. The old stock permission left the catalog with the stock workflow; permanent
 * deletion remains a separate, non-grantable administrator permission.
 *
 * Both mechanic roles receive this list as the garage service. The decision in
 * `docs/adr/0217-manager-auto-part-receipts.md` deliberately reuses the
 * same list for the temporary manager role: it needs the established workflow, not a second
 * manager-only copy that could drift from the route guards.
 */
const AUTO_PARTS_PERMISSIONS = ['autoParts.manage'] as const;

/**
 * The readings report for both mechanic roles (ADR 0241), with the two reads the book requires:
 * the owner gave the mechanics the book «in full», so they see what it carries — the garage's
 * «Показания» tab and vehicle requests — rather than an export of screens they lack. One list, so
 * the two roles cannot drift apart on it.
 */
const MECHANIC_READINGS_REPORT_PERMISSIONS = [
  'vehicleRequests.read',
  'vehicleReadings.read',
  'vehicleReadings.export',
] as const;

/**
 * Журнал путевых листов ведут те же, кто выписывает листы и ведёт водителей (ADR 0037).
 *
 * Пустого бланка (`waybills.issueBlank`) здесь нет намеренно: на начальном этапе его выписывает
 * только администратор (ADR 0071). Лист без задания — расход номера строгой отчётности ни на что,
 * и пока не видно, как часто он нужен, право не раздаётся вместе с журналом.
 */
const WAYBILL_PERMISSIONS = ['waybills.read', 'waybills.cancel', 'waybills.files'] as const;

/**
 * Что делает с заявкой на обслуживание оргтехники её заказчик (ADR 0085): заводит, правит и
 * удаляет. Ход заявки — назначение сервиса, согласование сметы, приёмка — сюда не входит: это
 * работа ответственного за оргтехнику, и приходит она надстройкой роли (ADR 0086).
 *
 * Список разворачивают и роли с осью области, и офисный `manager` (ADR 0181), у которого оси нет.
 * Разницу между ними держит не состав прав, а слой стороны: у ролей с осью действия сужает их
 * площадка или отдел, у менеджера — авторство (`actsAsRequestCustomer`). Заводить второй,
 * «офисный» список ради этого не нужно и вредно: круг заказчика у них буквально один и тот же, а
 * две копии разошлись бы первым же новым правом заявителя.
 */
const SERVICE_REQUEST_CUSTOMER_PERMISSIONS = [
  'serviceRequests.read',
  'serviceRequests.create',
  'serviceRequests.update',
  'serviceRequests.delete',
  // Фотография поломки — половина заявки: без неё сервис едет смотреть, вместо того чтобы везти
  // запчасть. Поэтому вложения заказчик подшивает наравне с исполнителем.
  'serviceRequests.files',
  /*
   * Сообщение о технике, которой нет в справочнике (ADR 0165, план кандидатов Р8; план предмета
   * заявки, Р10 и этап Э6) — выпуск B готовой, но закрытой волны: код написан и покрыт тестами
   * ещё 04.09.2026, а не работал ровно потому, что права не было ни у одной роли.
   *
   * ПРАВО СТОИТ В ОБЩЕМ СПИСКЕ ЗАКАЗЧИКА, А НЕ ПОИМЁННО У ПЯТИ РОЛЕЙ. «Не нашёл аппарат в
   * справочнике» — это тупик того же человека на том же шаге, где он выбирает предмет заявки:
   * разведи мы их, часть заказчиков упиралась бы в тупик без выхода, и объяснить разницу между
   * штабом и отделом было бы нечем. Ролей, разворачивающих этот список, шесть — пять площадочных
   * и отдельских (`shtab`, `rukstroy`, `site`, `department`, `department_head`) и офисный
   * `manager`, которому круг заявителя выдан ролью 10.09.2026; администратору право приходит
   * полным перечнем (`admin: [...PERMISSIONS]`), и строка здесь его не касается.
   *
   * ОБА ТРЕБОВАНИЯ `PERMISSION_REQUIRES` ВЫПОЛНЕНЫ, и это не совпадение: `serviceRequests.create`
   * стоит строкой выше в этом же списке, а `officeEquipment.read` есть у каждой из пяти ролей —
   * без него они не выбрали бы аппарат и в обычной заявке. Комендант список не разворачивает
   * вовсе: оргтехника приезжает ему переводом на `site` (`ROLE_MIGRATIONS`), и там расширение
   * объявлено поимённо.
   *
   * ЧТО ПРАВО ОТКРЫВАЕТ, А ЧТО НЕТ. Только третью ветвь предмета заявки — «аппарата нет в
   * справочнике», — и то не в одиночку: приём сообщений закрыт серверным рубильником
   * `office_equipment_candidate_intake` (Р10), и до его включения `POST /service-requests` с телом
   * кандидата отвечает 403 даже держателю права. Ни очереди проверки, ни решений по сообщению оно
   * не даёт — это `officeEquipment.review`, и живёт оно в наборе «Оргтехника: ведение».
   */
  'officeEquipment.propose',
] as const;

const WASTE_REQUEST_PERMISSIONS = [
  'wasteRequests.read',
  'wasteRequests.create',
  'wasteRequests.update',
  'wasteRequests.delete',
  'wasteRequests.status',
  'wasteRequests.assignOperator',
  // Примечание исполнителя пишет и тот, кто ведёт заявку: об «будем после 15:00» диспетчер чаще
  // узнаёт звонком, а не из портала, и записывает сам (ADR 0053).
  'wasteRequests.operatorComment',
  /*
   * Разбор талонов (ADR 0114, Р25) — работа того же человека, что ведёт заявку: он назначает
   * исполнителя, он принимает закрытие, он же и сверяет предъявленную бумагу. Поэтому право стоит
   * в общем наборе, а не поимённо у менеджера с диспетчером: разведи мы их здесь, сверка досталась
   * бы одному из двух, и вторая половина диспетчерской видела бы заявку без замечаний — то есть
   * ровно так же, как исполнитель.
   *
   * Ролей площадки в этом наборе нет и не появится: они заявку заводят, а не ведут. Внешний
   * исполнитель прав отсюда не получает вовсе — они приходят ему типом контрагента
   * (`COUNTERPARTY_TYPE_PERMISSIONS`), и разбор туда не попал намеренно (Р25).
   */
  'wasteRequests.ticketReview',
] as const;

/**
 * Ведение заявок на технику. Визы (`vehicleRequests.approve`) здесь нет намеренно: согласование —
 * решение заказчика со стороны объекта, а не того, кто заявку обрабатывает (ADR 0025).
 */
const VEHICLE_REQUEST_PERMISSIONS = [
  'vehicleRequests.read',
  'vehicleRequests.create',
  'vehicleRequests.update',
  'vehicleRequests.delete',
  'vehicleRequests.status',
] as const;

/**
 * Механизация глазами заказчика (план `docs/mechanization-module-plan.md`, Р9): просит технику,
 * правит и удаляет свою просьбу, пока она «Новая». Ход аренды — договорённость, выдача, возврат —
 * решает офис: за ними стоят деньги и звонок арендодателю, а не нужда площадки.
 *
 * Набор общий на все шесть ролей-заказчиков (площадка со своими переходными и обе роли отдела) —
 * так же, как в вывозе мусора: разойдись он, два заказчика одной площадки получили бы разный
 * портал на одну заявку.
 */
const MECH_REQUEST_CUSTOMER_PERMISSIONS = [
  'mechRequests.read',
  'mechRequests.create',
  'mechRequests.update',
  'mechRequests.delete',
] as const;

/**
 * Механизация глазами офиса: то же плюс ход аренды. Продления здесь нет — оно у диспетчера
 * поимённо (Р9), и общий набор раздал бы его менеджеру, то есть решил бы за заказчика вопрос,
 * которого он не задавал.
 */
const MECH_REQUEST_OFFICE_PERMISSIONS = [
  ...MECH_REQUEST_CUSTOMER_PERMISSIONS,
  'mechRequests.status',
] as const;

/**
 * Все семь прав модуля — ими же он и сужается областью (`MODULE_SCOPE`, Р10). Отдельным списком,
 * а не фильтром по префиксу: перечень читается как решение («вся механизация ходит по площадкам»),
 * а фильтр молча подхватил бы завтрашнее сквозное право — так `wasteRequests.ticketAudit` попал бы
 * под площадочную область, хотя заведён ровно наоборот, поперёк площадок.
 *
 * Виза (план визы, Р1) стоит здесь наравне с остальными: подписывают заявку своей площадки, и
 * держатель с пустой площадочной областью не должен видеть раздел ради одной кнопки.
 */
const MECH_REQUEST_PERMISSIONS = [
  ...MECH_REQUEST_OFFICE_PERMISSIONS,
  'mechRequests.extend',
  'mechRequests.approve',
] as const;

/**
 * **Ведение** недельных заявок — набор тех, кто неделю собирает: чтение вместе с правкой состава.
 * Общим списком, а не построчно у каждой роли, потому что у всех пяти он совпадает буквально:
 * кто ведёт модуль, тот и правит состав — и это осознанно. Развести чтение и правку внутри этого
 * набора значило бы отрезать от черновика второго штабиста и руководителя строительства той же
 * площадки, а частичный `UNIQUE (object_id, week_start)` не дал бы им завести свою заявку:
 * получился бы тупик, из которого выходят через администратора.
 *
 * Одно только `weeklyRequests.read` при этом стоит **шире** набора и выдаётся поимённо: наблюдатель,
 * обе роли отдела и арендодатель неделю читают, но не ведут. Причина в том, что документ переехал
 * в общий список «Заказ автотехники» и объясняет продления заказов, которые эти субъекты и так
 * видят; область им считает предикат видимости, а не право (у отдела — площадка отдела, у
 * арендодателя — наличие его машины в составе).
 *
 * Визы (`weeklyRequests.approve`) здесь нет намеренно — как и у заявок ТС: её раздают поимённо.
 * Удаления нет вовсе: до применения заявка снимается отменой с причиной, после — она история,
 * которую не стирают (архива у недельной заявки тоже нет).
 */
const WEEKLY_REQUEST_PERMISSIONS = [
  'weeklyRequests.read',
  'weeklyRequests.create',
  'weeklyRequests.update',
] as const;

/**
 * Права ролей. Перечислены полностью и явно, без наследования «роль X = роль Y плюс N прав»:
 * при наследовании новое право у базовой роли расходится по производным незаметно, а здесь
 * каждое расширение доступа видно в диффе строкой.
 *
 * «Менеджер» и «Диспетчер» сейчас совпадают по правам: по решению заказчика диспетчер ведёт
 * справочники наравне с менеджером, а заявки они и раньше вели одинаково. Роли оставлены
 * раздельными — они различаются организационно, и расходятся при первом же новом праве.
 */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  // Администратор — единственный, кто работает с архивом, откатами и учётками.
  admin: [...PERMISSIONS],

  manager: [
    ...DIRECTORY_PERMISSIONS,
    ...DRIVER_PERMISSIONS,
    ...WAYBILL_PERMISSIONS,
    ...WASTE_REQUEST_PERMISSIONS,
    ...VEHICLE_REQUEST_PERMISSIONS,
    // Механизация (Р9): менеджер ведёт аренду наравне с диспетчером — берёт в работу, отмечает
    // выдачу и завершает. Продления у него нет: оно у диспетчера одного, и в этом всё расхождение
    // двух ролей по модулю.
    ...MECH_REQUEST_OFFICE_PERMISSIONS,
    // Недельные заявки менеджер и диспетчер заводят за площадку и правят состав: половина недель
    // собирается звонком на диспетчерскую, и оформлять их должен тот, кому позвонили. Визы у них
    // нет — она остаётся решением объекта.
    ...WEEKLY_REQUEST_PERMISSIONS,
    // Гараж (ADR 0076) идёт вместе с водителями и листами: день парка распределяет тот же
    // человек, который заводит рейсы и выписывает бланки.
    'garage.read',
    // `docs/adr/0217-manager-auto-part-receipts.md`: manager is a temporary broad-access role.
    // Reusing the module permission keeps the web button and every server mutation on one rule;
    // irreversible deletion is still excluded by the separate `autoParts.delete` permission.
    ...AUTO_PARTS_PERMISSIONS,
    ...VEHICLE_READING_PERMISSIONS,
    // The readings report of the garage header (ADR 0241): the manager reads both modules the
    // book carries already, so the right adds the button and nothing else.
    'vehicleReadings.export',
    // Журнал ТО (Р14) — там же, где день парка: акт привозят диспетчерской, и заводит его тот, кто
    // ведёт машину в портале. У службы главного механика это право тоже есть, и оно не «её»: ТО
    // одной машины ведут с двух сторон, а не двумя журналами.
    ...VEHICLE_MAINTENANCE_PERMISSIONS,
    // Справочник оргтехники (ADR 0085) — та же работа со справочниками; модуль заявок по нему
    // менеджер не ведёт: ремонтом занимается ответственный за оргтехнику, а не диспетчер вывоза.
    'officeEquipment.read',
    'officeEquipment.write',
    // Перемещение вынесено из `write` своим правом (план перемещения из заявки, Р1) и стоит здесь
    // рядом с ним ради Р2: у обеих офисных ролей `write` есть по матрице, и выпуск, не дописавший
    // строку, отобрал бы у менеджера и диспетчера работающую сегодня операцию. Сузить круг —
    // отдельное решение заказчика (В1), а не побочный эффект выката.
    'officeEquipment.move',
    /*
     * Круг заявителя оргтехники — РОЛЬЮ, а не набором «Оргтехника: заявитель» (решение заказчика
     * 10.09.2026). Менеджер сидит в офисе рядом с той же техникой, что и диспетчер, и заводить
     * заявку на неё должен без похода к администратору за поимённой выдачей.
     *
     * ПОЧЕМУ РОЛЬЮ, ХОТЯ НАБОР ДЛЯ ЭТОГО И ЗАВЕДЁН. `manager` — временная роль-заплатка: заказчик
     * рано или поздно убирает её целиком, и всё, что она раздаёт, обязано уходить вместе с её
     * строкой в этой матрице. Набор пережил бы удаление роли строкой в `grant_roles` и выдачами в
     * `user_grants` — то есть мусором, который вычищают отдельной миграцией и о котором вспоминают
     * не в тот день. Диспетчера это решение не касается: у него круг по-прежнему приходит набором,
     * и обе половины «Заявителя» живут теперь бок о бок сознательно.
     *
     * ОБЛАСТЬ НЕ МЕНЯЕТСЯ И НЕ СУЖАЕТСЯ. Оси у роли нет, `serviceRequestVisibilityWhere` ей ничего
     * не сужает — менеджер видит заявки всей компании, и это ровно то, о чём просили: ни площадки,
     * ни отделы его не делят.
     *
     * А ВОТ ДЕЙСТВИЯ СУЖЕНЫ АВТОРСТВОМ, и держит это `actsAsRequestCustomer` (Р6 плана профилей):
     * правит, удаляет и подшивает файлы менеджер только у заявок, которые завёл сам. Страж
     * опознавал заявителя КОДОМ НАБОРА, и права, пришедшие ролью, мимо него прошли бы целиком —
     * поэтому вместе с этой строкой роль названа в `OFFICE_EQUIPMENT_ROLE_REQUESTERS`
     * (`service-requests.ts`). Две строки в двух файлах — цена того, что источник прав у профиля
     * стал вторым; развести их нельзя ничем, кроме как вернув выдачу набором.
     *
     * `officeEquipment.propose` приезжает вместе с кругом, потому что живёт в этом же списке
     * (ADR 0165): «не нашёл аппарат в справочнике» — тупик того же человека на том же шаге, где он
     * выбирает предмет заявки. Оба требования `PERMISSION_REQUIRES` выполнены строками выше —
     * `serviceRequests.create` списком и `officeEquipment.read` матрицей роли.
     */
    ...SERVICE_REQUEST_CUSTOMER_PERMISSIONS,
    'files.manageAny',
  ],

  dispatcher: [
    ...DIRECTORY_PERMISSIONS,
    ...DRIVER_PERMISSIONS,
    ...WAYBILL_PERMISSIONS,
    /*
     * Коррекция задним числом (ADR 0101, Р4) — первое право, которым диспетчер разошёлся с
     * менеджером, и разошёлся осознанно. Расхождение «в порталe одно, в жизни другое» видит
     * диспетчер: он собирает рейсы, ему звонят с объекта про не ту машину, и чинить это должен он,
     * а не тот, к кому надо идти. Поэтому право стоит здесь поимённо, а не в `WAYBILL_PERMISSIONS`:
     * общий набор раздал бы его и менеджеру, то есть решил бы за заказчика вопрос, которого он не
     * задавал. Предел глубины (`waybills.correctBeyondLimit`) остаётся администратору: за 30 днями
     * начинается разрыв хронологии журнала БСО, и объясняет его тот, кто за журнал отвечает (Р37).
     */
    'waybills.correct',
    ...WASTE_REQUEST_PERMISSIONS,
    ...VEHICLE_REQUEST_PERMISSIONS,
    /*
     * Механизация (Р9): ведение аренды — общее с менеджером, а продление — пятое расхождение с
     * ним, и решение это заказчика, а не нарезка прав. Продлить аренду значит согласиться платить
     * дальше; звонит арендодателю и договаривается диспетчер, поэтому право стоит здесь поимённо,
     * а не в общем наборе офиса.
     */
    ...MECH_REQUEST_OFFICE_PERMISSIONS,
    'mechRequests.extend',
    ...WEEKLY_REQUEST_PERMISSIONS,
    'garage.read',
    ...VEHICLE_READING_PERMISSIONS,
    ...VEHICLE_MAINTENANCE_PERMISSIONS,
    'officeEquipment.read',
    'officeEquipment.write',
    // То же, что у менеджера: право отделено от ведения справочника (Р1), а строка здесь сохраняет
    // диспетчеру сегодняшнюю возможность (Р2).
    'officeEquipment.move',
    'files.manageAny',
    /*
     * Возврат заявки в «Новую» и рассылки — второе и третье расхождение с менеджером, и оба по
     * той же причине, что коррекция: чинит и рассылает тот, кому звонят. Откат стирает нажитое
     * заявкой в работе (назначение, факт, рейс; виза остаётся, ADR 0172) и требует причины — но
     * требовать ради него администратора значит останавливать работу до чужого рабочего дня.
     * Предел глубины и удаление насовсем (`records.purge`) у администратора остаются.
     */
    'requests.rollbackStatus',
    'mailings.read',
    'mailings.manage',
  ],

  // Штаб — заказчик со стороны объекта: заявки заводит и правит (в пределах своего объекта),
  // но их ход — «в работе», «выполнена» — решают те, кто исполняет.
  shtab: [
    'directories.read',
    // Справочник оргтехники (ADR 0085) — только чтение: заявку заводят по конкретной единице, и
    // без карточек её не выбрать. Ведение справочника — у ответственного за оргтехнику.
    'officeEquipment.read',
    ...SERVICE_REQUEST_CUSTOMER_PERMISSIONS,
    'wasteRequests.read',
    'wasteRequests.create',
    'wasteRequests.update',
    'wasteRequests.delete',
    'vehicleRequests.read',
    'vehicleRequests.create',
    'vehicleRequests.update',
    'vehicleRequests.delete',
    // Механизация (Р9) — тот же заказчик, что и в вывозе: виброплиту просит площадка, а
    // договаривается о ней офис. Хода аренды у штаба нет, как и хода заявки вывоза.
    ...MECH_REQUEST_CUSTOMER_PERMISSIONS,
    // Недельная заявка — рабочий инструмент штаба: он и решает каждую неделю, что из стоящей на
    // площадке техники остаётся. Продление, которое даёт применение, обходит запрет объектным
    // ролям править работающий заказ — и это осознанное исключение: продлевает не тот, кто просил,
    // а виза руководителя строительства.
    ...WEEKLY_REQUEST_PERMISSIONS,
  ],

  // Руководитель строительства (ADR 0025, 0031): вторая роль заказчика на объекте. Заявки обоих
  // модулей заводит и правит наравне со штабом — и вывоз мусора, и технику; ход заявок, как и у
  // штаба, решают те, кто их исполняет. Своё у него одно — виза: без неё заявку на технику не
  // берут в работу.
  rukstroy: [
    'directories.read',
    'officeEquipment.read',
    ...SERVICE_REQUEST_CUSTOMER_PERMISSIONS,
    'wasteRequests.read',
    'wasteRequests.create',
    'wasteRequests.update',
    'wasteRequests.delete',
    'vehicleRequests.read',
    'vehicleRequests.create',
    'vehicleRequests.update',
    'vehicleRequests.delete',
    'vehicleRequests.approve',
    // Механизация — набором заказчика, тем же, что у штаба (ADR 0031): разойдись они, два
    // заказчика одной площадки получили бы разный портал на одну аренду.
    ...MECH_REQUEST_CUSTOMER_PERMISSIONS,
    // Виза аренды — то же, чем он отличается от штаба в заказе техники (план визы, Р2): без неё
    // аренду не берут в работу, ею же подписывается продление. Право стоит и в наборе «Виза
    // объекта» — там оно доедет до целевой роли `site`, — а здесь работает до перевода учёток.
    // Снять его отсюда, оставив в наборе, нельзя: набор до этапа 8 не действует, и виза не
    // работала бы ни у кого.
    'mechRequests.approve',
    // Неделю руководитель строительства собирает наравне со штабом и визирует один: его подпись —
    // единственное согласование на весь пакет, и она же двигает сроки. Своя заявка применяется в
    // момент подачи (правилом автовизы объектной роли); администратор под него не подпадает —
    // право визы у него есть, но действует он не за объект (ADR 0032).
    ...WEEKLY_REQUEST_PERMISSIONS,
    'weeklyRequests.approve',
  ],

  // Комендант — третий заказчик на объекте, но только по мусору: контейнеры и вывоз на площадке
  // ведёт он, а техника заказывается через штаб и руководителя строительства. Устроен как штаб
  // без модуля «Заказ ТС» — так же ограничен своими объектами и так же не ведёт ход заявок.
  // Отдельная роль, а не «штаб, которому не показали вкладку»: модуль закрывается правом на
  // чтение, и иначе комендант видел бы чужой заказ техники по прямой ссылке.
  commandant: [
    'directories.read',
    'wasteRequests.read',
    'wasteRequests.create',
    'wasteRequests.update',
    'wasteRequests.delete',
    // Механизация (Р9) — второй модуль коменданта, и это не расширение его должности, а её
    // предмет: компрессор и тепловая пушка нужны на площадке тому же, кто отвечает за её быт.
    // «Заказ ТС» ему по-прежнему закрыт целиком — технику заказывают через штаб.
    ...MECH_REQUEST_CUSTOMER_PERMISSIONS,
  ],

  /*
   * Площадка (ADR 0112) — целевая объектная роль реформы: одна вместо трёх. Состав не придуман, а
   * **вычтен**: `site` = права `shtab` минус то, что уезжает в два системных набора каталога
   * (`ROLE_GRANTS`, `grants.ts`). Таблица соответствия плана §9 требует точного равенства, и оно
   * сходится в обе стороны:
   *
   * | Было         | Стало                                        | Прав |
   * | ------------ | -------------------------------------------- | ---: |
   * | `shtab`      | `site` + «Заказ техники»                     | 11+7 = 18 |
   * | `rukstroy`   | `site` + «Заказ техники» + «Виза объекта»   | 11+7+2 = 20 |
   * | `commandant` | `site`                                       | 5 → 11 |
   *
   * Первые две строки — тождества, и их держит тест эквивалентности (`permissions.test.ts`).
   * Третья — единственное объявленное расширение доступа всей реформы (решение №1 заказчика от
   * 17.08.2026): комендант получает оргтехнику — справочник на чтение и заявки на обслуживание, то
   * есть ровно то, что есть у штаба помимо техники. Наступает расширение на этапе 8, когда учётки
   * переводят; здесь роль заводится и никому не назначается.
   *
   * Чего в наборе нет и не будет: `garage.read` и права журнала листов (§10 «спорное»)  — за ними
   * персональные данные водителей, и выдаются они отдельным решением, а не заодно с реформой.
   */
  site: [
    'directories.read',
    // Оргтехника — та половина штаба, которая к технике отношения не имеет: карточки читают, чтобы
    // выбрать единицу в заявке на ремонт, а саму заявку заводят и ведут как заказчик (ADR 0085).
    'officeEquipment.read',
    ...SERVICE_REQUEST_CUSTOMER_PERMISSIONS,
    // Вывоз мусора — общая часть всех трёх старых ролей: единственный модуль, который был и у
    // коменданта. Хода заявок площадка не решает, как и раньше: `wasteRequests.status` здесь нет.
    'wasteRequests.read',
    'wasteRequests.create',
    'wasteRequests.update',
    'wasteRequests.delete',
    /*
     * Механизации здесь БОЛЬШЕ НЕТ (план `docs/mechanization-approval-and-grants-plan.md`, Р4):
     * модуль ушёл из состава роли и выдаётся набором «Заказ механизации» — поимённо и отзывно
     * галочкой. Ответ заказчика «закрыть площадкам» выражается ровно этой строкой, точнее её
     * отсутствием: новые площадочные учётки заводятся на `site` (`ACTIVATION_DEFAULTS`), и модуль
     * им теперь выдаёт администратор осознанно.
     *
     * У трёх упраздняемых ролей выше (`shtab`, `rukstroy`, `commandant`) модуль остаётся до
     * перевода: набор им нельзя объявить совместимым (страж ролевых наборов), а снять право, не
     * имея чем его вернуть, значило бы молча отобрать работу. Перевод выдаёт набор взведённым
     * заранее (`ROLE_MIGRATIONS`), поэтому тождества §9 реформы сходятся: у `site` модуля нет ни с
     * одной стороны.
     */
  ],

  // Отдел (ADR 0040) — заказчик со стороны офиса: заявки на технику заводит от отдела, а не от
  // площадки. Вывоз мусора у роли есть (ADR 0062), но заказчиком там всегда объект — значит
  // работает право только у отдела с площадками (их бывает несколько, ADR 0144), а у остального
  // офиса раздел закрыт пустой
  // областью (`canUse`), а не отсутствием строки в матрице: право отвечает «что учётка делает»,
  // и состояние справочника в него не заводят.
  department: [
    'directories.read',
    'officeEquipment.read',
    ...SERVICE_REQUEST_CUSTOMER_PERMISSIONS,
    'wasteRequests.read',
    'wasteRequests.create',
    'wasteRequests.update',
    'wasteRequests.delete',
    'vehicleRequests.read',
    'vehicleRequests.create',
    'vehicleRequests.update',
    'vehicleRequests.delete',
    /*
     * Механизация (Р9, Р17): отдел просит технику **на свою площадку** — место эксплуатации
     * обязательно и выбирается из площадок отдела (ADR 0144). Отсюда и следствие, ради которого
     * права модуля стоят в `MODULE_SCOPE`: отдел, за которым площадок нет, раздела не видит вовсе
     * — ни в меню, ни по прямой ссылке.
     */
    ...MECH_REQUEST_CUSTOMER_PERMISSIONS,
    // Только чтение недели, и только своих площадок (ADR 0062; набором — ADR 0144): отдел
    // работает на них наравне со штабом и должен понимать, чем они заняты на неделе вперёд.
    // Ведения и визы у него нет — неделю собирает и подписывает площадка. Сколько недель он
    // читает, решает набор площадок его отделов: это и есть принятое следствие ADR 0144.
    'weeklyRequests.read',
  ],

  // Руководитель отдела (ADR 0040): та же пара «заказчик и визирующий», что «Штаб» и
  // «Руководитель строительства» на объекте. Отличается от сотрудника отдела ровно одним правом —
  // визой, — и совпадение остального закреплено тестом-сравнением, а не перечислением. По вывозу
  // мусора роли равны намеренно (ADR 0062): виза — право модуля «Заказ ТС», и на площадке штаб с
  // руководителем строительства по мусору тоже совпадают.
  //
  // Недельную заявку обе роли отдела **читают** — площадку своего отдела (ADR 0062), не более
  // того. Ведения и визы у них нет: неделя описывает занятость площадки, решает по ней штаб, а
  // виза руководителя строительства двигает сроки заказов объекта — это не «такое же
  // согласование, только от офиса». Отдел без площадки не видит ни одной недели: область пуста.
  department_head: [
    'directories.read',
    'officeEquipment.read',
    ...SERVICE_REQUEST_CUSTOMER_PERMISSIONS,
    'wasteRequests.read',
    'wasteRequests.create',
    'wasteRequests.update',
    'wasteRequests.delete',
    'vehicleRequests.read',
    'vehicleRequests.create',
    'vehicleRequests.update',
    'vehicleRequests.delete',
    'vehicleRequests.approve',
    // Механизация — тем же набором заказчика, что у сотрудника отдела, плюс виза (план визы, Р5):
    // заявку своего отдела руководитель подписывает наравне с площадкой, на которой она стоит.
    // Продление идущей аренды он при этом не решает — там своя область (Р10).
    ...MECH_REQUEST_CUSTOMER_PERMISSIONS,
    'mechRequests.approve',
    'weeklyRequests.read',
  ],

  // Внешний исполнитель (ADR 0010, ADR 0038) — единственная роль, чьи права роль задаёт не
  // целиком: модульные права приходят из типа его контрагента (COUNTERPARTY_TYPE_PERMISSIONS).
  // Здесь остаётся только общее для любого исполнителя: без чтения справочников не отрисовать
  // ни фильтр, ни название типа ТС в списке.
  operator: ['directories.read'],

  // Наблюдатель (ADR 0033) — сквозной просмотр обоих модулей без единого действия: заявки всех
  // объектов видны, но ни завести, ни изменить, ни продвинуть по статусу их нельзя. Объекта у
  // роли нет намеренно: она заводится ради общей картины по компании, а не работы на площадке.
  observer: [
    'directories.read',
    'officeEquipment.read',
    'wasteRequests.read',
    'vehicleRequests.read',
    // Третий модуль заявок — той же сквозной картиной по компании, без единого действия.
    'serviceRequests.read',
    // Четвёртый — механизация (Р9): что арендуем сейчас и во сколько это обходится, видно тем же
    // одним чтением. Ни завести, ни продлить, ни завершить аренду наблюдатель не может.
    'mechRequests.read',
    // Недельная заявка живёт в общем списке «Заказ автотехники» и объясняет, откуда взялись
    // продления заказов, которые наблюдатель и так видит целиком. Одно право чтения, без набора
    // `WEEKLY_REQUEST_PERMISSIONS`: заводить и править неделю роль не может ничего.
    'weeklyRequests.read',
  ],

  /**
   * Водитель (ADR 0102) — второй контур портала: своё задание на дату и свои показания, и ничего
   * больше. Два права, и отсутствие третьего здесь важнее их обоих: `directories.read` есть у
   * всех остальных ролей, а этой не выдаётся — кабинет справочники не читает, все подписи ему
   * приходят готовыми с сервера.
   *
   * Область у роли своя, четвёртая (`PERSON_SCOPED_ROLES`): «строка про самого работника».
   * Предиката в `lib/access.ts` у неё нет — она держится тем, что кабинет не принимает `personId`
   * ни в каком виде и берёт его из принципала.
   */
  driver: ['driverCabinet.read', 'driverCabinet.submit'],

  /*
   * The chief mechanic's service (ОГМ). Both roles see the whole fleet: they have no area axis — a
   * mechanic answers for the company's equipment, not for a site, and has no «own» object by
   * position. That is why they are not in `OBJECT_SCOPED_ROLES`.
   *
   * What the service reads: the directories (equipment with everything bound to it — kinds, types,
   * models, specifications, categories — plus objects and departments) by `directories.read`, the
   * fleet on a date by `garage.read`, the waybill journal with printing by `waybills.read`, driver
   * cards by `drivers.read`, and since ADR 0241 readings and vehicle requests (below).
   *
   * Neither role runs the equipment directory, and that is not an oversight: the model has no
   * right for equipment alone, and `directories.write` opens the **whole** section — objects,
   * counterparties, tariffs. Until the right is split, a mechanic reads equipment and whoever runs
   * the directories registers it.
   *
   * The service runs two things. The maintenance journal (Р14) comes as its own pair of rights,
   * not through reading readings: the maintenance form must not depend on the readings module.
   * Auto-part receipts (receipts plan, Р4): the customer named the service, not a person, so the
   * right is given by role and the module has no grant set.
   *
   * Readings and vehicle requests came with the readings report (ADR 0241): the owner gave the book
   * to the mechanics «in full», and the book carries both modules (`PERMISSION_REQUIRES` of
   * `vehicleReadings.export`). So the service sees the garage's «Показания» tab and the «Заказ ТС»
   * section read-only; it still neither corrects a reading nor accepts a day
   * (`vehicleReadings.write` stays with those who run the fleet's day).
   */
  mechanic: [
    'directories.read',
    'garage.read',
    'waybills.read',
    'drivers.read',
    ...VEHICLE_MAINTENANCE_PERMISSIONS,
    // Auto-part receipts (receipts plan, Р4) come by role, not by a grant set: the customer named
    // the mechanics, not a person. The dispatcher deliberately lacks them: the dispatcher corrects
    // a maintenance act, while spending on parts is run by the chief mechanic's service.
    ...AUTO_PARTS_PERMISSIONS,
    ...MECHANIC_READINGS_REPORT_PERMISSIONS,
  ],

  /*
   * Главный механик — он же глава ОГМ. От механика отличается ровно тем, что ведёт: карточки
   * водителей (`drivers.write`) и списание испорченного бланка (`waybills.cancel`). Печать и
   * просмотр листов у обоих одинаковые; коррекция задним числом остаётся у диспетчера с
   * администратором — это правка чужой работы, а не ведение парка.
   */
  chief_mechanic: [
    'directories.read',
    'garage.read',
    'waybills.read',
    'waybills.cancel',
    'drivers.read',
    'drivers.write',
    // Журнал ТО у обоих одинаковый: обслуживание ведёт служба, а не её глава (Р14).
    ...VEHICLE_MAINTENANCE_PERMISSIONS,
    // Auto-part receipts — for the same reason and with the same list (receipts plan, Р4).
    ...AUTO_PARTS_PERMISSIONS,
    // The readings report with its two reads (ADR 0241), the same list as the mechanic's.
    ...MECHANIC_READINGS_REPORT_PERMISSIONS,
  ],
};

/**
 * Права, которые даёт тип контрагента учётки внешнего исполнителя (ADR 0038). Каждый тип —
 * один модуль портала, и основа набора в нём одна и та же: видеть свои заявки и закрывать
 * выполненное. Заводить, править и удалять заявки исполнитель не может ни в одном модуле —
 * заказчик и исполнитель разведены, и это граница модели, а не особенность вывоза мусора.
 * Примечание оператора (ADR 0053) её не двигает: оно не меняет ни предмет заявки, ни её ход —
 * это строка исполнителя в комментарии, и своё право у неё именно поэтому.
 *
 * Record по всем типам, а не по тем, у кого учётки есть: новый тип контрагента обязан здесь
 * появиться строкой и ответить на вопрос «а этот что исполняет». Пустой список — «учёток у
 * такого контрагента не бывает»: генподрядчик и подрядчик живут в справочнике как реквизиты
 * договора, работают в портале их сотрудники под собственными ролями.
 */
export const COUNTERPARTY_TYPE_PERMISSIONS: Record<CounterpartyType, readonly Permission[]> = {
  general_contractor: [],
  contractor: [],
  // Оператор вывоза (ADR 0010): видит заявки, назначенные его контрагенту, закрывает их и пишет
  // в них своё примечание (ADR 0053) — заявку это не редактирует, границу исполнителя не двигает.
  operator: ['wasteRequests.read', 'wasteRequests.status', 'wasteRequests.operatorComment'],
  // Арендодатель ТС (ADR 0038): видит заявки, на которые вышла его техника, и закрывает их.
  //
  // Недельная заявка открывается ему тем же и единственным фактором — наличием его машины в
  // составе; область считает предикат, а не это право. Заводить и визировать неделю он не может:
  // площадка решает за себя сама, а состав документа он видит только своими строками.
  vehicle_lessor: ['vehicleRequests.read', 'vehicleRequests.status', 'weeklyRequests.read'],
  /*
   * Арендодатель механизации (план `docs/mechanization-module-plan.md`, Р6) — **пустой список, и
   * это решение, а не заготовка**: учёток за таким контрагентом не заводят вовсе, вход в портал
   * ему не открывают, а строка справочника нужна затем, чтобы заявка знала, кому звонить, и чтобы
   * деньги складывались по арендодателям.
   *
   * Пустота здесь работает: `COUNTERPARTY_TYPES_WITH_ACCOUNTS` считается из непустых списков, и
   * форма учётки такого контрагента не предложит. Впиши сюда `mechRequests.read` — и появился бы
   * вход, которого никто не заводил, с областью, которой у модуля нет: своего предиката по
   * арендодателю механизация не имеет, аренду видно по площадке.
   */
  mech_lessor: [],
  // Поставщик (ADR 0051) — сторона договора поставки: в портале за него никто не работает, его
  // склады ведут изнутри. Пустой список, как у генподрядчика и подрядчика; появится у поставщика
  // свой модуль — он будет строкой здесь, а не новой ролью.
  supplier: [],
  // Сервисная компания (ADR 0085) — исполнитель заявок на обслуживание оргтехники: видит
  // назначенные ему заявки, ведёт смету, двигает свою часть цикла и подшивает акт со счётом.
  //
  // Справочника оргтехники у сервиса нет намеренно: «его» техника в справочнике ничем не отмечена,
  // и право означало бы доступ ко всему парку компании — реквизиты нужной единицы приходят
  // снимком в самой заявке. Заводить и править заявки он тоже не может: заказчик и исполнитель
  // разведены во всех трёх модулях, и это граница модели (ADR 0038).
  service: [
    'serviceRequests.read',
    /*
     * Деньги и объём работ (план `docs/office-equipment-requester-card-plan.md`, Р2) — это его
     * собственная смета и его счёт: у подрядчика их не отбирают, а называют правом. Без строки
     * выкат проекции забрал бы у сервисной компании то, что она сама и написала, — и заметилось бы
     * это на первой же карточке, где исполнитель не видит выставленной им суммы.
     */
    'serviceRequests.finance',
    'serviceRequests.estimate',
    'serviceRequests.status',
    'serviceRequests.files',
  ],
};

/**
 * Права, которые даёт надстройка роли (ADR 0086) — третья ось субъекта доступа.
 *
 * `Record` по всем надстройкам, как и у типов контрагента: новая надстройка обязана появиться
 * строкой и ответить на вопрос «что именно она добавляет». Пустой список означал бы надстройку,
 * которую можно выдать, ничего при этом не выдав, — такой в матрице быть не должно.
 *
 * **Оба набора самодостаточны** (план `docs/office-equipment-access-profiles-plan.md`, Р4): чтение
 * своего модуля и справочника лежит в составе, а не приходит от базовой роли. Так было не всегда —
 * до этого состав опирался на то, что все три совместимые роли дают чтение сами
 * (`ROLE_ADDON_BASE_ROLES`), и держался, стало быть, на совпадении: снятие права из роли молча
 * выключило бы половину набора, а выдать набор роли без модуля было нельзя вовсе.
 */
export const ROLE_ADDON_PERMISSIONS: Record<RoleAddon, readonly Permission[]> = {
  office_equipment_operator: [
    /*
     * САМОДОСТАТОЧНОСТЬ НАБОРА (план `docs/office-equipment-access-profiles-plan.md`, Р4; находка
     * Н4). Шесть прав чтения и ведения заявки, которых в составе не было: набор давал
     * `officeEquipment.write` без `.read` и решения по заявке без её чтения. Работало это не
     * потому, что так задумано, а потому, что совместимых базовых ролей ровно три
     * (`ROLE_ADDON_BASE_ROLES`) и у каждой чтение есть по матрице, — то есть на совпадении.
     *
     * Держателям набора не добавляется ни одного права: все шесть даёт любая из трёх ролей, а
     * источники соединены `OR` («отобрать» в этой модели означает «не выдать»). Пустоту дельты
     * доказывает тест `grants-contracts.test.ts`, а не этот абзац.
     *
     * Зачем тогда правка. Пока состав опирался на роль: профиль нельзя выдать роли без прав модуля
     * (Р5 плана — этап Э7 расширил `grant_roles` на менеджера и диспетчера), барьер требований
     * проверял не то, что кажется (Н2), а снятие права из роли молча выключило бы половину набора.
     */
    'officeEquipment.read',
    'serviceRequests.read',
    'serviceRequests.create',
    'serviceRequests.update',
    'serviceRequests.delete',
    'serviceRequests.files',
    // Справочник ведёт он, а не общий держатель справочников (ADR 0085 §3).
    'officeEquipment.write',
    /*
     * Перемещение своим правом (план `docs/office-equipment-move-from-request-plan.md`, Р1, Р2).
     * Держателю набора оно ничего не добавляет и добавить не может: перемещение он записывает и
     * сегодня — по `officeEquipment.write`, который стоит строкой выше. Строка здесь ради того,
     * чтобы выпуск, отделивший перемещение от ведения, не отобрал у «Ведения» работу, которой оно
     * пользуется каждый день.
     */
    'officeEquipment.move',
    /*
     * Проверка сообщений об отсутствующей технике (ADR 0165, план кандидатов Р8; план предмета
     * заявки, этап Э6): очередь, правка реквизитов и три решения — подтвердить карточкой,
     * объединить с существующей, отклонить.
     *
     * ЗДЕСЬ, А НЕ В ИТ-СЛУЖБЕ, И ЭТО ГЛАВНЫЙ ЗАМОК ВОЛНЫ. Подтверждение ЗАВОДИТ единицу парка,
     * то есть делает ровно то, что охраняет `officeEquipment.write`; `PERMISSION_REQUIRES` так
     * его и объявляет, а `write` есть только у этого набора — у ИТ-службы его нет намеренно
     * («сисадмин не ведёт справочник»). Выдай мы проверку ИТ-службе, у справочника появился бы
     * второй, необъявленный вход — тот самый, ради устранения которого волна и затевалась.
     *
     * Требование выполняется составом набора, а не базовой ролью: `officeEquipment.write` стоит
     * двумя строками выше, и барьер выдачи пройдёт даже там, где у роли модуля нет вовсе.
     *
     * Рубильником приёма проверка НЕ ограждается (Р10): выключение прекращает приток сообщений,
     * но разобрать уже накопленное обязаны — иначе аварийное выключение оставило бы заявителей
     * без ответа по тому, что они уже отправили.
     */
    'officeEquipment.review',
    // Решения по заявке: кого позвать, согласны ли на эти деньги, принята ли работа. Права сметы
    // здесь нет намеренно — смету пишет исполнитель, а оператор её согласует; выданные одному
    // субъекту, эти два права превратили бы согласование в подпись под собственной работой.
    'serviceRequests.assign',
    'serviceRequests.approveEstimate',
    'serviceRequests.status',
    /*
     * Раскладка присланного письмом объёма работ по графам (план
     * `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р2; ответ В9 от
     * 09.09.2026) — и это НЕ отмена абзаца строкой выше про права сметы.
     *
     * `serviceRequests.estimate` «Ведению» по-прежнему не выдаётся: оно означает «я автор объёма
     * работ», то есть сторону исполнителя, и вместе с `approveEstimate` двумя строками выше
     * превратило бы согласование в подпись под собственной работой. Право раскладки устроено
     * иначе — держателя автором сметы оно не делает и открывает ровно одну ручку: перенос перечня
     * из письма подрядчика в графы, с подъёмом ревизии и новой подписью там, где ревизия уже
     * согласована.
     *
     * Больше никому не выдаётся (Р2 прямым текстом): ни ИТ-службе, ни набору исполнителя, ни
     * сервисной компании — те объём работ пишут, а не переписывают чужой.
     */
    'serviceRequests.estimateRewrite',
    /*
     * Деньги и объём работ (план `docs/office-equipment-requester-card-plan.md`, Р2): он согласует
     * объём работ и принимает работу — суммы и есть предмет его должности. Право стоит рядом с
     * решениями по заявке, а не среди чтения, потому что без него две соседние строки означали бы
     * «согласуй, не глядя на цифры».
     */
    'serviceRequests.finance',
    /*
     * Заморозка и срочность — своими правами (волна В5 плана переработки заявок). Оба раньше
     * приходили внутри других: «Отложена» — вместе с `status`, флаг «Срочная» — вместе с `update`,
     * то есть всякому, кто правит заявку. Отобрать их у соседних должностей составом набора было
     * нечем, и потому они выделены; здесь они возвращаются тому, у кого и были, — иначе правка
     * матрицы прав отобрала бы у «Ведения» то, чем оно пользуется каждый день.
     */
    'serviceRequests.hold',
    'serviceRequests.urgency',
    /*
     * Плановые закупки расходников (план расходников и закупки, Р12) — решение заказчика: закупку
     * заводит тот, кто ведёт модуль. Отдельным набором её не выделяли намеренно: «Оргтехника:
     * номенклатура» ведёт перечень позиций и остаток на полке, а закупка — документ снабжению, и
     * подписывает его тот, кто отвечает за модуль целиком.
     *
     * Требование `officeEquipment.read` (`PERMISSION_REQUIRES`) держателям набора выполняется
     * любой из трёх базовых ролей (`ROLE_ADDON_BASE_ROLES`: штаб, площадка, отдел) — чтение
     * справочника есть у каждой.
     *
     * Права выданы раньше маршрутов, и это осознанно: до выпуска ручек закупки набор не открывает
     * ни одной новой двери — право без ручки не даёт ничего, зато миграция каталога и состав в
     * контрактах едут вместе и не расходятся между волнами.
     */
    'officeEquipmentPurchases.manage',
    /*
     * Заявка без аппарата (план расходников и закупки, Р5) — работа того же держателя: он ведёт
     * модуль, и «принтер привезли, инвентарного номера ещё нет» приходит к нему. Требование
     * `serviceRequests.create` (`PERMISSION_REQUIRES`) у держателей набора выполняется любой из
     * трёх базовых ролей (`ROLE_ADDON_BASE_ROLES`: штаб, площадка, отдел): заведение заявки есть у
     * каждой из них как у заказчика.
     */
    'serviceRequests.createWithoutEquipment',
  ],
  /**
   * КООРДИНАЦИЯ модуля (Р51, Р54): сверх набора действий она расширяет и **область** — модуль
   * виден ей целиком (`ADDON_MODULE_WIDE_SCOPE`), и это свойство кода набора, а не его состава.
   *
   * ВИЗЫ ЗДЕСЬ БОЛЬШЕ НЕТ (Э9 плана профилей, миграция E): `serviceRequests.approveIt` снято.
   * Виза упразднена ещё планом упрощения цикла (Р10) — ручки `PATCH /:id/it-approval` нет, коридора
   * визы нет, — и право не открывало держателю ни одной двери. Держать его в составе значило
   * обещать администратору работу, которой не существует. Снять его раньше было нельзя: сторона
   * обсуждения «Системный администратор» опознавалась ровно этим правом, и уборка погасила бы её
   * молча; опознание переехало на код набора этапом Э3 (Р9), и только после этого право убрано.
   * Из словаря прав (`PERMISSIONS`, `PERMISSION_CATALOG`) и из матрицы администратора оно НЕ
   * снято — это другая работа с осиротевшими выдачами и чисткой каталога.
   *
   * Работа руками отсюда уехала (Э6 плана профилей): ходы исполнителя и документы живут в
   * `office_equipment_executor`, и держит его тот же человек — профиль «Системный администратор»
   * выдаётся парой кодов. Разделены они ровно потому, что область у половин разная: координация
   * смотрит на модуль сквозным, работа руками открывается назначением на конкретную заявку.
   *
   * Права сметы здесь нет и быть не может (Р55): подтверждать необходимость работы, которую сам же
   * и продаёшь, — не согласование. По той же причине его нет у сервисной компании.
   */
  office_equipment_it_approver: [
    /*
     * САМОДОСТАТОЧНОСТЬ (Р4, Н4 плана профилей): чтение своего модуля и справочника теперь в
     * составе, а не «придёт от базовой роли». Прежняя формулировка держалась на трёх совместимых
     * ролях и рассыпалась бы от первой же четвёртой.
     *
     * `serviceRequests.create` — не симметрия ради симметрии: `PERMISSION_REQUIRES` объявляет
     * `createWithoutEquipment` (он ниже) зависимым именно от него, потому что ручка у права чужая —
     * `POST /service-requests`. Набор без заведения не прошёл бы барьер требований у роли, где
     * заведения нет, и обещал бы то, чего сервер не пустит.
     *
     * Действующим держателям это не добавляет ни одного права: все три даёт любая из трёх базовых
     * ролей.
     */
    'officeEquipment.read',
    'serviceRequests.read',
    'serviceRequests.create',
    /*
     * Координация: `assign` — чтобы позвать исполнителя (в том числе назначив себя), `hold` —
     * чтобы придержать заявку, пока ждут запчасть.
     *
     * РАБОТЫ ИСПОЛНИТЕЛЕМ ЗДЕСЬ БОЛЬШЕ НЕТ (план `docs/office-equipment-access-profiles-plan.md`,
     * Р4, этап Э6; план аудита исполнителей, Н1 и Э8): `serviceRequests.execute` и `.files` уехали
     * в отдельный набор `office_equipment_executor` (`MODULE_GRANTS`, миграция 0262). Причина —
     * область, а не состав: у этого набора она сквозная (`ADDON_MODULE_WIDE_SCOPE`), и пока ходы
     * исполнителя лежали в нём, назначить человека исполнителем можно было только вместе с правом
     * читать предмет, переписку и суммы ВСЕХ заявок компании. Профиль «Системный администратор»
     * выдаётся теперь двумя кодами сразу и не теряет ни одного действия; действующим держателям
     * второй набор выдала та же миграция, что и разделила составы.
     *
     * Права `status` здесь нет и быть не должно: оно открывает **весь** операторский коридор —
     * приёмку работы и отмену из любого статуса, — то есть то, что по матрице §6 остаётся за
     * «Ведением». Ходы исполнителя приходят парой «назначение + `execute`», и ручки для этого
     * закрыты стражем «одно из прав», а не `status`.
     */
    'serviceRequests.assign',
    'serviceRequests.hold',
    /*
     * Cancel (workflow-fixes plan Р2) — a right of its own and not `status`: that one would open the
     * acceptance of the work and the rollbacks to the IT service, which it must not have (see
     * above). The arcs are those of the cancel of «Ведение»: «Новая», «В работе», «Отложена» — and
     * nothing more (`SERVICE_CANCEL_TRANSITIONS`). Handed to the set by the catalog migration
     * `*_service_request_cancel_grant.sql`.
     */
    'serviceRequests.cancel',
    /*
     * ЦЕЛЕВОЕ ПРАВО ЭТОГО НАБОРА (план `docs/office-equipment-move-from-request-plan.md`, Р1, Р2):
     * подтверждение перемещения. `officeEquipment.write` у ИТ-службы нет и не появляется (план
     * профилей, §5.2) — сисадмин ведёт заявки, а не парк, — и до сих пор это означало, что записать
     * переезд он не может вовсе: аппарат, который он сам же и перевёз, приходилось нести на правку
     * тому, кто ведёт справочник. Отдельное право снимает выбор «либо весь парк, либо ничего»;
     * ради этого оно и заведено.
     *
     * Это ЕДИНСТВЕННОЕ место выпуска, где право открывает новую дверь: у остальных получателей
     * (роли офиса, «Ведение») перемещение работало и раньше — по `officeEquipment.write`.
     */
    'officeEquipment.move',
    /*
     * Разбор очереди «Письма устройств» (план почтовой телеметрии, Р30). Здесь по той же
     * границе, что и подтверждение перемещения: сисадмин ведёт аппараты, а не справочник, и
     * решать «чьё это письмо» обязан он — он же и настраивал отправку на самом аппарате.
     */
    'officeEquipment.telemetry',
    /*
     * Деньги и объём работ (план `docs/office-equipment-requester-card-plan.md`, Р2): ИТ-служба
     * решает «чинить или менять» и ведёт инхаус-ремонт — оба вопроса читаются по составу работ и
     * цене. Правом, а не по назначению: назначение открыло бы ей суммы только тех заявок, где она
     * значится исполнителем, а решать «чинить или менять» она обязана ДО того, как кого-то
     * назначили.
     */
    'serviceRequests.finance',
    /*
     * Заявка без аппарата (Р5) — и у ИТ-службы тоже, решением заказчика: половина таких заявок
     * заводится её же руками («поставьте розетку», «настройте почту новому сотруднику»), и
     * заводить их через заказчика значило бы просить человека оформить работу, о которой он и сам
     * не просил. Область у набора сквозная (`ADDON_MODULE_WIDE_SCOPE`), поэтому заказчика такой
     * заявке ИТ-служба выбирает из обеих осей — и объекты, и отделы (Р6).
     */
    'serviceRequests.createWithoutEquipment',
  ],
};

/**
 * Типы контрагентов, к которым можно привязать учётку. Выводятся из матрицы, а не
 * перечисляются вторым списком: тип без прав — контрагент, за который в портале никто не
 * работает, и учётка на нём означала бы вход без единого действия.
 */
export const COUNTERPARTY_TYPES_WITH_ACCOUNTS = COUNTERPARTY_TYPES.filter(
  (type) => COUNTERPARTY_TYPE_PERMISSIONS[type].length > 0,
);

export function counterpartyTypeHasAccounts(
  type: CounterpartyType | null | undefined,
): type is CounterpartyType {
  return !!type && COUNTERPARTY_TYPE_PERMISSIONS[type].length > 0;
}

/**
 * Кому проверяется право: роль плюс тип контрагента учётки. Принимать сюда одну роль нельзя —
 * у внешнего исполнителя роль без контрагента отвечает только за общую часть прав, и вызов
 * `can(role, ...)` молча возвращал бы «нет» на его собственный модуль. Поэтому субъект —
 * объект: и принципал на сервере, и текущий пользователь на портале подходят под него как есть.
 */
export interface AccessSubject {
  role: Role | null;
  /** Тип контрагента учётки; у ролей вне `COUNTERPARTY_SCOPED_ROLES` не читается. */
  counterpartyType?: CounterpartyType | null;
  /**
   * Надстройки роли (ADR 0086): набор дополнительных прав поверх роли. Необязателен — учёток без
   * надстроек большинство, и требовать пустой массив от каждого вызова `can` значило бы менять
   * все места, где субъектом служит роль как есть.
   */
  addons?: readonly RoleAddon[] | null;
  /**
   * Эффективные права назначенных полномочий (ADR 0106, шаг 1c) — четвёртый источник субъекта.
   *
   * Приходят **готовыми**, а не выводятся здесь из кодов наборов, и это не сокращение: состав
   * набора живёт в базе (`grant_permissions`), матрица его не знает и знать не должна — иначе
   * `permissions.ts` перестал бы быть данными и начал зависеть от таблиц. Считает список сервер
   * (`grantPermissionsExpr`), и там же, в соединении с `grant_roles`, стоит **гейт совместимости с
   * ролью**: набор, выданный до смены роли, прав больше не даёт. Поэтому здесь роль повторно не
   * спрашивается — второй гейт с другим ответом хуже, чем один.
   *
   * Тип — `Permission[]`, хотя в базе право хранится текстом: сироту (право, снятое из словаря
   * выкатом) отсеивает читатель на границе `isPermission`. Матрица получает уже отфильтрованное —
   * иначе `permissionsFor` и витрина начали бы рассказывать про доступ то, чего в коде нет.
   *
   * Необязательное — и это условие совместимости шага, а не удобство. Субъектом по-прежнему бывает
   * «роль как есть»: так его собирают `ACCESS_PROFILES`, тесты матрицы и портал до переключения
   * `/auth/me` на эффективные права (§10.1 плана). Субъект без поля считается ровно как сегодня.
   */
  grantPermissions?: readonly Permission[] | null;
  /**
   * Коды назначенных наборов (ADR 0106) — то, ЧТО учётке выдано, в отличие от `grantPermissions`,
   * которое отвечает, что она в итоге может.
   *
   * **Матрица прав это поле НЕ читает, и это решение, а не недоделка** (план профилей оргтехники,
   * Р9). Состав набора живёт в базе; влияй он на `can`, состав определял бы ответ двумя путями
   * сразу — правами набора и его кодом, — и первый же выкат, поменявший состав, развёл бы эти два
   * пути молча. Поэтому код отвечает только на вопросы, которых у матрицы нет вовсе: «какой у
   * человека бизнес-профиль модуля» (сторона обсуждения, подпись, будущее письмо) и «видит ли он
   * модуль сквозным» (`hasModuleWideScope`, область — не право).
   *
   * Строки, а не `SystemGrantCode`: рядом с системными в базе лежат наборы, собранные
   * администратором, и сузить тип значило бы соврать про содержимое.
   *
   * Необязательное — как `addons` и `grantPermissions`: субъектом по-прежнему бывает «роль как
   * есть», и требовать пустой массив от каждого вызова `can` значило бы менять все места, где
   * права спрашивают у роли.
   */
  grantCodes?: readonly string[] | null;
}

// Проверка прав идёт на каждом запросе, поэтому списки сразу разложены по множествам.
const ROLE_PERMISSION_SETS = new Map<Role, ReadonlySet<Permission>>(
  ROLES.map((role) => [role, new Set(ROLE_PERMISSIONS[role])]),
);

const COUNTERPARTY_PERMISSION_SETS = new Map<CounterpartyType, ReadonlySet<Permission>>(
  COUNTERPARTY_TYPES.map((type) => [type, new Set(COUNTERPARTY_TYPE_PERMISSIONS[type])]),
);

const ROLE_ADDON_PERMISSION_SETS = new Map<RoleAddon, ReadonlySet<Permission>>(
  ROLE_ADDONS.map((addon) => [addon, new Set(ROLE_ADDON_PERMISSIONS[addon])]),
);

/**
 * Есть ли у субъекта право. Учётка без роли не может ничего: доступ выдаётся ролью, и пока её
 * не назначили, пользователь для портала — никто (активировать такую учётку API не даёт).
 * Исполнитель без контрагента — то же самое в своём модуле: без контрагента неизвестно ни что
 * он исполняет, ни чьи заявки видит, поэтому модульных прав у него нет (активировать такую
 * учётку API тоже не даёт).
 *
 * Источников четыре, и спрашиваются они в одном порядке: **роль → надстройки → наборы →
 * тип контрагента**. Порядок влияет только на то, каким источником право объясняется первым
 * (`permissionSource`); на ответ «есть или нет» — нет, потому что источники соединены OR и никакой
 * из них права не отнимает (инвариант 1 §8: «отобрать = не выдать»).
 */
export function can(subject: AccessSubject | null | undefined, permission: Permission): boolean {
  const role = subject?.role;
  if (!role) return false;
  if (ROLE_PERMISSION_SETS.get(role)?.has(permission)) return true;
  // Надстройки роли (ADR 0086) — третий источник прав, и спрашивается он до контрагента:
  // надстройка бывает у любой базовой роли, а тип контрагента читается только у исполнителя.
  // Несовместимую с ролью надстройку сюда не пропускает API учёток, но проверка стоит и здесь:
  // матрица обязана отвечать одинаково на любой субъект, откуда бы он ни пришёл.
  for (const addon of subject?.addons ?? []) {
    if (canAttachAddon(role, addon) && ROLE_ADDON_PERMISSION_SETS.get(addon)?.has(permission)) {
      return true;
    }
  }
  /*
   * Назначенные полномочия (ADR 0106, шаг 1c) — четвёртый источник, и стоит он **рядом с
   * надстройками**, а не на их месте. Причина в самом шаге: 1c переключает чтение на новые таблицы,
   * но старую запись не прекращает (это 1d), а `user_role_addons` живёт до 1e. Значит одна и та же
   * выдача двух системных наборов в этот релиз видна и надстройкой, и набором, и оба источника
   * отвечают «да» на одно право. Совпадение здесь — норма перехода, а не ошибка: OR даёт на него
   * тот же ответ, что каждый источник по отдельности, и снятие любого из двух источников доступ не
   * меняет. Убери мы старый источник заодно с добавлением нового — шаг перестал бы быть
   * переключением чтения и стал бы 1c+1d одним релизом, без возможности откатиться на версию,
   * читающую надстройки.
   *
   * Роль здесь не проверяется намеренно: список уже посчитан сервером с гейтом совместимости
   * (`grantPermissionsExpr`, соединение с `grant_roles`), и второй гейт в матрице означал бы два
   * ответа на один вопрос. Отсутствие поля — «наборов у субъекта нет»: `?? []` ниже, а не приведение
   * к пустому массиву на каждом вызове.
   *
   * `includes` по короткому массиву, а не `Set` на каждый вызов: множество здесь пришлось бы
   * собирать заново при каждом `can` (список приходит на субъекте, а не лежит константой), и это
   * дороже линейного перебора полусотни строк — а больше их не бывает, назначаемых прав всего 53.
   */
  if ((subject?.grantPermissions ?? []).includes(permission)) return true;
  if (!isCounterpartyScopedRole(role)) return false;
  const type = subject?.counterpartyType;
  return !!type && (COUNTERPARTY_PERMISSION_SETS.get(type)?.has(permission) ?? false);
}

/**
 * Откуда у субъекта право: роль, надстройка, назначенное полномочие или тип контрагента. Нужно
 * витрине прав (`docs/permissions-tab-plan.md`): «право есть» и «право пришло полномочием, а не
 * должностью» — разные ответы, и при пересмотре ролей важен второй.
 *
 * Живёт рядом с `can` и повторяет его порядок источников намеренно: вторая копия этого порядка на
 * портале разъехалась бы с первой при первой же правке модели, и витрина начала бы объяснять
 * доступ неправильно — то есть ровно тогда, когда по ней принимают решение.
 */
export interface PermissionOrigin {
  kind: 'role' | 'addon' | 'grant' | 'counterparty';
  /** Надстройка, давшая право; заполнена только при `kind: 'addon'`. */
  addon?: RoleAddon;
  /**
   * Код набора, давшего право; при `kind: 'grant'` заполнен настолько, насколько субъект вообще
   * знает разбивку.
   *
   * На шаге 1c он не заполнен ни у кого, и это не забытая ветка. Субъект несёт **объединение**
   * эффективных прав своих наборов (`grantPermissions`): так их считает SQL — `array_agg(DISTINCT
   * gp.permission)`, — потому что одно право штатно лежит в двух наборах учётки, и на вопрос «есть
   * ли доступ» разбивка не влияет. Поле объявлено здесь потому, что вопрос витрины про наборы
   * звучит иначе — «какой именно набор снять, чтобы право ушло», — и отвечать на него будет тот же
   * тип, когда субъект начнёт нести разбивку по наборам. Тип — `string`, а не `SystemGrantCode`:
   * рядом с двумя системными в базе лежат наборы, собранные администратором, и сузить его значило
   * бы соврать про содержимое; заодно словарь прав не начинает зависеть от `grants.ts` — эта
   * зависимость односторонняя (полномочия знают про права, права про полномочия — нет).
   */
  grantCode?: string;
}

/**
 * **Все** источники, давшие субъекту право, в порядке `can`: роль → надстройки → наборы →
 * контрагент. Пустой список означает «права нет» — ровно то же, что `can(...) === false`.
 *
 * Множественная, а не «первый найденный», и это требование модели, а не удобство (§8 плана). В
 * свободной сборке одно право приходит одновременно от роли и нескольких наборов; на переходных
 * шагах 1a–1d — ещё и от надстройки, из которой набор перенесён. «Первый найденный» на таком
 * субъекте отвечает не неточно, а **неверно**: витрина объясняет доступ должностью, хотя набор даёт
 * то же право, а расчёт «что снимется при отзыве» получается прямо обратным — снятие набора отберёт
 * право только если его не даёт больше никто, и узнать это можно лишь пересчитав все источники
 * (`effectiveDelta`).
 *
 * Надстройка перечисляется по разу на каждую совместимую с ролью надстройку, давшую право; наборы —
 * одной записью на весь их вклад, потому что разбивки по наборам субъект на этом шаге не несёт
 * (см. `grantCode`).
 */
export function permissionSources(
  subject: AccessSubject | null | undefined,
  permission: Permission,
): readonly PermissionOrigin[] {
  const role = subject?.role;
  if (!role) return [];
  const origins: PermissionOrigin[] = [];
  if (ROLE_PERMISSION_SETS.get(role)?.has(permission)) origins.push({ kind: 'role' });
  for (const addon of subject?.addons ?? []) {
    if (canAttachAddon(role, addon) && ROLE_ADDON_PERMISSION_SETS.get(addon)?.has(permission)) {
      origins.push({ kind: 'addon', addon });
    }
  }
  if ((subject?.grantPermissions ?? []).includes(permission)) origins.push({ kind: 'grant' });
  if (isCounterpartyScopedRole(role)) {
    const type = subject?.counterpartyType;
    if (type && COUNTERPARTY_PERMISSION_SETS.get(type)?.has(permission)) {
      origins.push({ kind: 'counterparty' });
    }
  }
  return origins;
}

/**
 * Первый источник права или `null` — обёртка над `permissionSources`, сохранённая ради читателей.
 *
 * Оставлена намеренно, а не «пока не переписали»: её зовут карточка доступа
 * (`apps/web/src/pages/admin/AccessPeopleTab.tsx`, одна строка «откуда право») и тест витрины, и
 * менять их этим шагом нельзя — шаг 1c переключает источник, а не читателей. Обёрткой, а не второй
 * реализацией: разъехавшись, две копии порядка источников дали бы разные ответы на один субъект.
 *
 * Читателям с наборами она отвечает неполно **по построению** — в этом и смысл появления
 * множественной версии рядом. Переходить на `permissionSources` витрина обязана в том же шаге, где
 * начинает показывать наборы; до тех пор ответ у неё прежний, потому что и субъект прежний.
 */
export function permissionSource(
  subject: AccessSubject | null | undefined,
  permission: Permission,
): PermissionOrigin | null {
  return permissionSources(subject, permission)[0] ?? null;
}

/** Что изменится в правах субъекта: только то, что реально появится и реально уйдёт. */
export interface PermissionDelta {
  added: Permission[];
  removed: Permission[];
}

/**
 * Разница эффективных прав двух состояний субъекта — то, что показывает предпросмотр перед
 * сохранением выдачи (§8, §12 плана).
 *
 * Считается по **всем** источникам, а не по спискам прав наборов, и это единственный способ
 * ответить правильно: снятие набора отбирает право только если его не даёт больше никто — ни роль,
 * ни вторая надстройка, ни другой набор, ни тип контрагента. Разница списков наборов на том же
 * субъекте показала бы «уйдёт `officeEquipment.write`» держателю роли «Менеджер», у которого это
 * право ролевое, — то есть напугала бы администратора отзывом, ничего не меняющим, и одновременно
 * промолчала бы о правах, которые действительно уходят вместе с последним их источником.
 *
 * Принимает субъекты целиком, а не пару списков прав: субъект — это роль, надстройки, наборы и
 * контрагент вместе, и «что будет после» строится из него подстановкой одного изменившегося поля
 * (`{ ...subject, grantPermissions: [...] }`), а не пересчётом прав на стороне вызывающего. Второй
 * такой пересчёт и был бы той самой копией конвейера источников, от которой избавляет эта функция.
 *
 * Порядок в обоих списках — словарный (`PERMISSIONS`), а не порядок пришедших наборов: предпросмотр
 * читают глазами, и строки в нём не должны переставляться от того, в каком порядке отмечали
 * галочки. Перебором словаря, а не пересечением множеств: словарь закрыт, прав в нём под шесть
 * десятков — один проход дешевле двух наборов.
 */
export function effectiveDelta(
  before: AccessSubject | null | undefined,
  after: AccessSubject | null | undefined,
): PermissionDelta {
  const added: Permission[] = [];
  const removed: Permission[] = [];
  for (const permission of PERMISSIONS) {
    const had = can(before, permission);
    const has = can(after, permission);
    if (had === has) continue;
    (has ? added : removed).push(permission);
  }
  return { added, removed };
}

/**
 * Субъект вместе с его областью — то, чем открывается раздел портала (`canUse`). Наборы
 * необязательны: под него подходят как есть и принципал на сервере, и текущий пользователь на
 * портале, а роль без области их и не читает.
 */
export interface ScopedSubject extends AccessSubject {
  /** Прямая привязка объектной роли (ADR 0039). */
  constructionObjectIds?: readonly string[];
  /** Производная привязка роли отдела (ADR 0062): объекты её отделов. */
  departmentObjectIds?: readonly string[];
}

/**
 * Объекты, в пределах которых субъект работает в модуле «Вывоз мусора» (ADR 0062): у объектной
 * роли — свои, у роли отдела — площадки её отделов. `null` означает «областью не ограничен» и
 * отличается от пустого набора, как «видит всё» отличается от «не видит ничего».
 *
 * Ось производная и общая трём модулям: вывоз мусора (ADR 0062), механизация (ADR 0152) и — с
 * ADR 0201 — заказ спецтехники, где заказчиком тоже всегда стоит объект. Грузоперевозку отдел
 * по-прежнему заводит и от себя: там у заявки два рода заказчика, и площадка не отменяет отдел.
 */
export function placeObjectScopeIds(
  subject: ScopedSubject | null | undefined,
): readonly string[] | null {
  const role = subject?.role;
  if (isObjectScopedRole(role)) return subject?.constructionObjectIds ?? [];
  if (isDepartmentScopedRole(role)) return subject?.departmentObjectIds ?? [];
  return null;
}

function hasPlaceObjectScope(subject: ScopedSubject): boolean {
  const ids = placeObjectScopeIds(subject);
  return ids === null || ids.length > 0;
}

/**
 * Есть ли субъекту чем работать в журнале путевых листов (ADR 0192).
 *
 * Свой предикат, а не `hasPlaceObjectScope`, и разница ровно в оси отдела. Тот меряет отдел
 * **площадками** его отделов — так устроены вывоз и механизация, где заказчиком всегда стоит
 * объект. У листов ось производная и шире: лист виден отделу и по талону своего отдела
 * (`vehicle_requests.department_id`), у которого объекта нет вовсе, и по талону площадки его отдела
 * (решение Р3 ADR 0192). Возьми мы площадочный предикат — отдел без площадок потерял бы раздел,
 * хотя собственные заявки на технику он заводит и бумага по ним выписывается.
 *
 * Отсюда `true` у роли отдела без оговорок: пустой эта ось не бывает — учётка роли отдела обязана
 * иметь хотя бы один отдел (`users.ts`, `refine` на `departmentIds`), и другого способа её завести
 * портал не знает. Объектная роль — наоборот: площадка без объектов область имеет пустую, и раздел
 * ей закрыт так же, как закрыт вывоз мусора отделу без площадок.
 */
function hasWaybillScope(subject: ScopedSubject): boolean {
  if (isObjectScopedRole(subject.role)) return (subject.constructionObjectIds ?? []).length > 0;
  return true;
}

/** Права модуля, который весь ходит по площадкам, — одной строкой на модуль. */
const placeScoped = (
  permissions: readonly Permission[],
): [Permission, (subject: ScopedSubject) => boolean][] =>
  permissions.map((permission) => [permission, hasPlaceObjectScope]);

/**
 * Права, применимость которых зависит от области (ADR 0062). Права, которого здесь нет, область
 * не сужает — тот же приём, что в `ROLE_VEHICLE_REQUEST_TYPES`: новое право открыто всем, у кого
 * оно есть, кроме тех, кому его сузили осознанно, строкой здесь.
 */
const MODULE_SCOPE = new Map<Permission, (subject: ScopedSubject) => boolean>([
  ...placeScoped(WASTE_REQUEST_PERMISSIONS),
  /*
   * Механизация (план `docs/mechanization-module-plan.md`, Р10) — та же площадочная область, что
   * у вывоза, и по той же причине: ось модуля одна, `object_id` места эксплуатации, а отдел
   * работает на площадках своего отдела (ADR 0062, ADR 0144).
   *
   * **Именно эти строки и закрывают раздел отделу без площадок** — ответ заказчика «отдел без
   * площадки не увидит даже вкладку модуля». Без них право у роли было бы, а работать им не над
   * чем: `canUse` отвечал бы «да», пункт меню рисовался, а список приходил бы пустым всегда.
   */
  ...placeScoped(MECH_REQUEST_PERMISSIONS),
  /*
   * Путевые листы (ADR 0192): раздел открывается площадке набором полномочия, и площадка без
   * объектов видела бы пункт меню, за которым всегда пусто. Строка одна — у модуля одно право,
   * которым он открывается, а остальные (`cancel`, `files`, `correct`) площадке не выдаются вовсе и
   * области не требуют: их держит диспетчерская, у которой оси нет.
   */
  ['waybills.read', hasWaybillScope],
]);

/**
 * Открыт ли субъекту раздел: право **и** непустая область, в которой это право применимо
 * (ADR 0062). Отделу без площадки вывоз мусора закрыт именно так — право у роли есть, работать
 * им не над чем.
 *
 * Спрашивается там, где решается «показывать ли раздел»: пункт меню, маршрут портала, стартовая
 * страница. Действия внутри раздела спрашивают `can`: область по конкретной строке всё равно
 * проверяет сервер, а пустой она к этому моменту уже не бывает.
 */
export function canUse(subject: ScopedSubject | null | undefined, permission: Permission): boolean {
  if (!subject || !can(subject, permission)) return false;
  const hasScope = MODULE_SCOPE.get(permission);
  return !hasScope || hasScope(subject);
}

/**
 * Работает ли учётка от контрагента указанного типа (ADR 0038) — то есть исполняет ли она
 * заявки этого модуля. Предикат, а не сравнение с ролью: «оператор вывоза» — это роль
 * исполнителя плюс контрагент-оператор, и одна роль без типа означает уже не то же самое.
 */
export function actsForCounterparty(
  subject: AccessSubject | null | undefined,
  type: CounterpartyType,
): boolean {
  return isCounterpartyScopedRole(subject?.role) && subject?.counterpartyType === type;
}

/**
 * Выдан ли субъекту набор с таким кодом (ADR 0106) — вопрос БИЗНЕС-ПРОФИЛЯ, а не доступа.
 *
 * Стоит рядом с `actsForCounterparty` по той же причине: это предикат о субъекте, а не источник
 * прав. `can` его не зовёт и звать не должен (см. `AccessSubject.grantCodes`); зовут его те, кому
 * нужно опознать «кто этот человек в модуле» — сторона обсуждения, подпись, адресат письма.
 * Отдельная функция, а не `includes` по месту: мест таких несколько, и каждое из них обязано
 * одинаково понимать субъекта без кодов («наборов нет»), а не падать на `undefined`.
 */
export function hasGrantCode(subject: AccessSubject | null | undefined, code: string): boolean {
  return (subject?.grantCodes ?? []).includes(code);
}

/** Все права субъекта — для отладки и для страницы учёток. */
export function permissionsFor(subject: AccessSubject | null | undefined): readonly Permission[] {
  const role = subject?.role;
  if (!role) return [];
  return PERMISSIONS.filter((permission) => can(subject, permission));
}

/**
 * Все различимые субъекты доступа: роли без контрагента как есть, роль от контрагента —
 * по разу на каждый тип контрагента с учётками. Список нужен там, где перебирают «всех, кто
 * бывает в портале»: тесты матрицы, обратный поиск по праву.
 */
export const ACCESS_PROFILES: readonly AccessSubject[] = [
  ...ROLES.flatMap((role) =>
    isCounterpartyScopedRole(role)
      ? COUNTERPARTY_TYPES_WITH_ACCOUNTS.map((counterpartyType) => ({ role, counterpartyType }))
      : [{ role }],
  ),
  // Надстройка (ADR 0086) даёт свой различимый субъект: «штаб» и «штаб с надстройкой» отвечают
  // на `can` по-разному, и тесты доступа обязаны перебирать оба. По разу на каждую пару
  // «базовая роль × надстройка» — сочетания надстроек друг с другом прав не меняют.
  /*
   * Коды наборов идут ТЕМИ ЖЕ строками, что надстройки, и это не дубль поля (план профилей
   * оргтехники, Р9). С шага 1c одна и та же выдача видна и надстройкой, и набором: код системного
   * набора совпадает с кодом надстройки по построению — `SYSTEM_GRANT_CODES` объявлен
   * `satisfies readonly RoleAddon[]` ровно ради этого равенства. Перебор обязан нести обе половины,
   * потому что читатели у них разные: права спрашивают надстройку, бизнес-профиль модуля — код
   * (сторона обсуждения `it` и `operator`). Оставь мы здесь одни надстройки — профиль перестал бы
   * опознаваться стороной у КАЖДОГО субъекта перебора, и выглядело бы это дефектом сторон, а не
   * перебора.
   */
  ...ROLE_ADDONS.flatMap((addon) =>
    ROLE_ADDON_BASE_ROLES[addon].map((role) => ({
      role,
      addons: [addon] as const,
      grantCodes: [addon] as const,
    })),
  ),
];

/** Субъекты, у которых есть указанное право (обратный поиск: подсказки интерфейса и тесты). */
export function profilesWith(permission: Permission): AccessSubject[] {
  return ACCESS_PROFILES.filter((subject) => can(subject, permission));
}

/** Человекочитаемое имя субъекта: у исполнителя оно называет предмет работы, а не роль. */
export function accessProfileLabel(subject: AccessSubject): string {
  if (subject.role && isCounterpartyScopedRole(subject.role) && subject.counterpartyType) {
    return `${roleLabels[subject.role]} — ${counterpartyTypeLabels[subject.counterpartyType]}`;
  }
  if (!subject.role) return 'Без роли';
  // Надстройки (ADR 0086) дописываются к роли, а не заменяют её: человек остаётся штабом своего
  // объекта, и в списке учёток это должно читаться именно так — «Штаб + Оператор (оргтехника)».
  const addons = (subject.addons ?? []).map((addon) => roleAddonLabels[addon]);
  return [roleLabels[subject.role], ...addons].join(' + ');
}

/**
 * Роли, которым в модуле «Заказ ТС» доступны не все типы заявки (ADR 0040). Отдел заказывает
 * грузоперевозки всегда, а спецтехнику — только пока за его отделами закреплена хоть одна
 * площадка (ADR 0201): заказ «работы на площадке» адресуют объекту, и отделу без площадок
 * адресовать его некуда.
 *
 * Таблица, а не право `vehicleRequests.freight` и не `if` по имени роли. Право отвечает «что
 * учётка делает», а здесь ограничение по признаку самой строки — это область; в матрице оно
 * завело бы колонку-исключение, которую пришлось бы читать при каждом новом типе заявки.
 * Отсутствие роли в таблице означает «доступны все типы» — так новый тип заявки открывается
 * всем, кроме тех, кому его закрыли осознанно, строкой здесь.
 */
const ROLE_VEHICLE_REQUEST_TYPES: Partial<Record<Role, readonly VehicleRequestType[]>> = {
  department: ['freight_transport'],
  department_head: ['freight_transport'],
};

/**
 * Типы заявки на технику, доступные субъекту (пустой список невозможен: роли без модуля сюда не
 * доходят).
 *
 * Коридор роли **расширяется её областью**, а не снимается таблицей: спецтехника открыта отделу
 * ровно тогда, когда за его отделами стоят площадки (ADR 0201). Заказчиком у такой заявки
 * остаётся объект (`vehicle_requests_department_freight_check` не даёт другого), и площадки —
 * единственное, что делает этот заказ адресуемым.
 *
 * Отсюда и площадочный субъект: ответ зависит от справочника, а не только от роли. Отдел, с
 * которого сняли последнюю площадку, отвечает как прежде — «только грузоперевозки», — и вкладка
 * «На объекте» у него исчезает тем же ответом, каким появилась.
 */
export function allowedVehicleRequestTypes(
  subject: ScopedSubject | null | undefined,
): readonly VehicleRequestType[] {
  const own = subject?.role ? ROLE_VEHICLE_REQUEST_TYPES[subject.role] : undefined;
  if (!own) return VEHICLE_REQUEST_TYPES;
  // Площадки спрашиваются тем же `placeObjectScopeIds`, что и в вывозе с механизацией: три модуля
  // ходят по одной производной оси, и второй способ её посчитать разошёлся бы с ними молча.
  if (isDepartmentScopedRole(subject?.role) && (placeObjectScopeIds(subject)?.length ?? 0) > 0) {
    return VEHICLE_REQUEST_TYPES;
  }
  return own;
}

export function canOrderVehicleRequestType(
  subject: ScopedSubject | null | undefined,
  requestType: VehicleRequestType,
): boolean {
  return allowedVehicleRequestTypes(subject).includes(requestType);
}

/**
 * Роли с собственным коридором статусов: право «менять статус» у них есть, но не на весь
 * рабочий цикл. Таблица, а не исключение в коде: следующая такая роль добавляется строкой.
 */
const ROLE_STATUS_TRANSITIONS: Partial<Record<Role, Record<RequestStatus, RequestStatus[]>>> = {
  // Внешний исполнитель закрывает то, что сам выполнил; подтверждать и отменять — решения
  // заказчика (ADR 0010). Коридор один на оба модуля: закрытие выполненного — это одно и то же
  // действие, что у вывоза мусора, что у аренды техники, поэтому таблица по роли, а не по типу
  // контрагента.
  operator: OPERATOR_STATUS_TRANSITIONS,
};

/**
 * Право «двигать заявку по статусам» — **по модулю** (план `docs/mechanization-module-plan.md`,
 * Р9). Таблицей, а не дизъюнкцией `wasteRequests.status || vehicleRequests.status`, которая стояла
 * здесь до механизации: та знала ровно два права, и третьему модулю его собственное
 * `mechRequests.status` не открыло бы ни одного хода — коридор считался бы по чужим правам, а
 * держатель прав вывоза получил бы ходы в аренде, которой не ведёт.
 *
 * `Record` по модулям: новый модуль заявок не соберётся, пока не ответит, каким правом он ходит.
 */
const REQUEST_STATUS_PERMISSION: Record<RequestModule, Permission> = {
  waste: 'wasteRequests.status',
  vehicle: 'vehicleRequests.status',
  mech: 'mechRequests.status',
};

/**
 * Смена статуса хотя бы в одном модуле заявок — гейт **только** для ролей со своим коридором
 * (`ROLE_STATUS_TRANSITIONS`), то есть сегодня для внешнего исполнителя.
 *
 * Почему он не спрашивает модуль, хотя таблица выше есть. Модуль исполнителю выбирает не право, а
 * тип его контрагента: оператор вывоза видит только заявки вывоза, арендодатель ТС — только
 * заказы со своей машиной (ADR 0038), и до чужого модуля он не доходит ни ручкой, ни выборкой.
 * Спроси мы здесь право модуля, ответ изменился бы ровно для несуществующего случая — «оператору
 * вывоза про заказ техники», — то есть коридор сузился бы там, где его и так нет.
 *
 * Дизъюнкция считается **по той же таблице**, а не вторым перечнем прав: разойдись они, новый
 * модуль открыл бы исполнителю ходы, о которых не спрашивали. Роль без контрагента (`operator` без
 * типа) прав статуса не имеет вовсе — и своего коридора ей этот гейт не открывает.
 */
function canChangeAnyRequestStatus(has: (permission: Permission) => boolean): boolean {
  return REQUEST_MODULES.some((module) => has(REQUEST_STATUS_PERMISSION[module]));
}

/**
 * Строка таблицы переходов по статусу заявки — единственное место, где эти таблицы читаются.
 *
 * Смотрит на таблицу `Partial`-взглядом сознательно, хотя тип обещает строку на каждый статус.
 * Обещание верно только внутри одной сборки: `RequestStatus` — словарь собранного бандла, а
 * значение приходит из типа `request_status` базы, и на выкате они расходятся. Так и случилось с
 * «Завершена» (ADR 0135, миграции 0194/0195): база получила новое значение и перевела в него
 * выполненные заявки, а у людей в браузере оставался прошлый выпуск портала — статуса такого он
 * не знал, спред `undefined` бросал `TypeError`, и падала не ячейка, а весь список заявок.
 *
 * Неизвестный статус означает «ходов отсюда нет»: строка показывается, меню переходов не
 * открывается, остальной список работает. Смену статуса всё равно решает сервер — портал лишь
 * предлагает ходы, и предложить их по незнакомому статусу ему нечем.
 */
function corridorFrom(
  table: Record<RequestStatus, RequestStatus[]>,
  from: RequestStatus,
): RequestStatus[] {
  return (table as Partial<Record<RequestStatus, RequestStatus[]>>)[from] ?? [];
}

/** Ходы по циклу, открытые правом ведения статусов: без завершения — оно отпирается другим. */
function cycleTransitions(
  from: RequestStatus,
  subject: AccessSubject,
  module: RequestModule,
  has: (permission: Permission) => boolean,
): RequestStatus[] {
  const ownCorridor = subject.role ? ROLE_STATUS_TRANSITIONS[subject.role] : undefined;
  // Свой коридор заменяет общий целиком — вместе с вопросом о праве: у исполнителя оно приходит
  // типом контрагента, и модуль ему выбирает заявка, а не право (см. `canChangeAnyRequestStatus`).
  if (ownCorridor) return canChangeAnyRequestStatus(has) ? corridorFrom(ownCorridor, from) : [];
  if (!has(REQUEST_STATUS_PERMISSION[module])) return [];
  const forward = corridorFrom(requestStatusTransitions[module], from);
  return has('requests.rollbackStatus')
    ? [...forward, ...corridorFrom(requestStatusRollbacks[module], from)]
    : forward;
}

/**
 * Статусы, доступные субъекту из текущего статуса (пустой список — смена статуса запрещена).
 * Живёт рядом с матрицей, а не с таблицами переходов: «кто может» — это право, а таблицы
 * переходов описывают только «что за чем идёт».
 *
 * Модуль обязателен: с ADR 0135 коридоры «Вывоза мусора» и «Заказа ТС» разошлись, и вопрос
 * «куда отсюда можно» без него не имеет одного ответа.
 *
 * **Завершение отпирает не ведение цикла, а разбор талонов** (`wasteRequests.ticketReview`).
 * Точку в заявке ставит тот, кто признал бумагу: он один видел талоны, сверку и расхождения, —
 * а `wasteRequests.status` есть и у внешнего исполнителя, который эту бумагу принёс. Отсюда и
 * порядок: ход в «Завершена» из таблицы цикла вычитается и возвращается отдельным правом.
 */
export function allowedStatusTransitions(
  from: RequestStatus,
  subject: AccessSubject,
  module: RequestModule,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): RequestStatus[] {
  const cycle = cycleTransitions(from, subject, module, has).filter((s) => s !== 'completed');
  if (module !== 'waste' || from !== 'done') return cycle;
  return has('wasteRequests.ticketReview') ? [...cycle, 'completed'] : cycle;
}

export function canTransitionStatus(
  from: RequestStatus,
  to: RequestStatus,
  subject: AccessSubject,
  module: RequestModule,
  has: (permission: Permission) => boolean = (permission) => can(subject, permission),
): boolean {
  return allowedStatusTransitions(from, subject, module, has).includes(to);
}
